import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import NotificationSystem from './NotificationSystem';
import BarcodeScanner from './BarcodeScanner';
import ReceiptScanner from './ReceiptScanner';
import HistoriqueAchats from './HistoriqueAchats';
import PrixMagasinModal from './PrixMagasinModal';
import EstimationModal from './EstimationModal';
import ConfigurationArticleModal from './ConfigurationArticleModal';
import LanguageSwitcher from './LanguageSwitcher';
import SubscriptionPage from './SubscriptionPage';
import HouseholdPage from './HouseholdPage';
import AnalyticsDashboard from './AnalyticsDashboard';
import BudgetDashboard from './BudgetDashboard';
import ShoppingPlannerTab from './ShoppingPlannerTab';
import FeedbackSupportTab from './FeedbackSupportTab';
import AboutSmartInventoryTab from './AboutSmartInventoryTab';
import SmartOnboardingModal from './SmartOnboardingModal';
import FoodEducationSheetModal from './FoodEducationSheetModal';
import { useTranslation } from '../i18n/LanguageContext';
import { getFoodEducationSheet, inferNutritionReference } from '../services/foodEducationService';
import { findCiqualFoodByName } from '../data/ciqualEssentialFoods';
import { 
  Home, 
  Package,
  Plus, 
  ShoppingCart, 
  Minus, 
  AlertTriangle, 
  Brain,
  Mail,
  LogOut,
  Users,
  Coffee,
  Utensils,
  Calendar,
  Trash2,
  X,
  Settings,
  Edit3,
  Check,
  CreditCard,
  Info,
  BarChart3,
  Crown,
  ShieldCheck,
  Camera,
  Archive
} from 'lucide-react';

const PREDEFINED_ITEM_CATEGORIES = [
  { value: 'alimentation', label: 'Alimentation' },
  { value: 'boisson', label: 'Boisson' },
  { value: 'laitier', label: 'Produits laitiers' },
  { value: 'boulangerie', label: 'Boulangerie' },
  { value: 'viande_poisson', label: 'Viande & Poisson' },
  { value: 'plats_prepares', label: 'Plats préparés' },
  { value: 'desserts', label: 'Desserts & Sucreries' },
  { value: 'conserve', label: 'Conserves' },
  { value: 'hygiene', label: 'Hygiène' },
  { value: 'entretien', label: 'Entretien' },
  { value: 'fruits_legumes', label: 'Fruits & Légumes' },
];

// Catalogue prédéfini d'articles par catégorie pour le popup de suggestions
const PREDEFINED_CATALOG = [
  // Fruits & Légumes
  { nom: 'Pommes vertes', categorie: 'fruits_legumes', unite: 'kg', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Pommes', categorie: 'fruits_legumes', unite: 'kg', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Poires', categorie: 'fruits_legumes', unite: 'kg', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Bananes', categorie: 'fruits_legumes', unite: 'kg', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Oranges', categorie: 'fruits_legumes', unite: 'kg', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Citrons', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Pasteque', categorie: 'fruits_legumes', unite: 'pieces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Tomates', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Carottes', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Pommes de terre', categorie: 'fruits_legumes', unite: 'kg', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Oignons', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Salade', categorie: 'fruits_legumes', unite: 'pièces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Concombre', categorie: 'fruits_legumes', unite: 'pieces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Courgettes', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Poivrons', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.3, group: 'fruits_legumes' },
  { nom: 'Piments', categorie: 'fruits_legumes', unite: 'pieces', seuil: 2, group: 'fruits_legumes' },
  { nom: 'Avocats', categorie: 'fruits_legumes', unite: 'pieces', seuil: 2, group: 'fruits_legumes' },
  { nom: 'Aubergines', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Mais', categorie: 'fruits_legumes', unite: 'pieces', seuil: 2, group: 'fruits_legumes' },
  { nom: 'Brocolis', categorie: 'fruits_legumes', unite: 'pieces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Ail', categorie: 'fruits_legumes', unite: 'pieces', seuil: 2, group: 'fruits_legumes' },
  { nom: 'Haricots', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Olives', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.3, group: 'fruits_legumes' },
  { nom: 'Fraises', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.3, group: 'fruits_legumes' },
  { nom: 'Myrtilles', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.2, group: 'fruits_legumes' },
  { nom: 'Melon', categorie: 'fruits_legumes', unite: 'pieces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Cerises', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.3, group: 'fruits_legumes' },
  { nom: 'Peches', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  { nom: 'Mangue', categorie: 'fruits_legumes', unite: 'pieces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Ananas', categorie: 'fruits_legumes', unite: 'pieces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Noix de coco', categorie: 'fruits_legumes', unite: 'pieces', seuil: 1, group: 'fruits_legumes' },
  { nom: 'Kiwis', categorie: 'fruits_legumes', unite: 'pieces', seuil: 4, group: 'fruits_legumes' },
  { nom: 'Raisins', categorie: 'fruits_legumes', unite: 'kg', seuil: 0.5, group: 'fruits_legumes' },
  // Épicerie & Courses
  { nom: 'Pain', categorie: 'boulangerie', unite: 'pièces', seuil: 2, group: 'epicerie' },
  { nom: 'Pain de mie', categorie: 'boulangerie', unite: 'pieces', seuil: 1, group: 'epicerie' },
  { nom: 'Croissants', categorie: 'boulangerie', unite: 'pieces', seuil: 2, group: 'epicerie' },
  { nom: 'Baguette', categorie: 'boulangerie', unite: 'pieces', seuil: 2, group: 'epicerie' },
  { nom: 'Pain plat', categorie: 'boulangerie', unite: 'pieces', seuil: 2, group: 'epicerie' },
  { nom: 'Bretzels', categorie: 'boulangerie', unite: 'pieces', seuil: 2, group: 'epicerie' },
  { nom: 'Bagels', categorie: 'boulangerie', unite: 'pieces', seuil: 2, group: 'epicerie' },
  { nom: 'Pancakes', categorie: 'boulangerie', unite: 'pieces', seuil: 4, group: 'epicerie' },
  { nom: 'Gaufres', categorie: 'boulangerie', unite: 'pieces', seuil: 4, group: 'epicerie' },
  // Viande & Produits de la mer
  { nom: 'Viande avec os', categorie: 'viande_poisson', unite: 'kg', seuil: 0.5, group: 'viande_poisson' },
  { nom: 'Cuisses de poulet', categorie: 'viande_poisson', unite: 'kg', seuil: 0.8, group: 'viande_poisson' },
  { nom: 'Steak', categorie: 'viande_poisson', unite: 'kg', seuil: 0.5, group: 'viande_poisson' },
  { nom: 'Crevettes', categorie: 'viande_poisson', unite: 'kg', seuil: 0.4, group: 'viande_poisson' },
  { nom: 'Sushi', categorie: 'viande_poisson', unite: 'pieces', seuil: 6, group: 'viande_poisson' },
  { nom: 'Huitres', categorie: 'viande_poisson', unite: 'pieces', seuil: 6, group: 'viande_poisson' },
  // Fast food & repas
  { nom: 'Hamburger', categorie: 'plats_prepares', unite: 'pieces', seuil: 2, group: 'plats_prepares' },
  { nom: 'Frites', categorie: 'plats_prepares', unite: 'kg', seuil: 0.5, group: 'plats_prepares' },
  { nom: 'Pizza', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  { nom: 'Hot-dog', categorie: 'plats_prepares', unite: 'pieces', seuil: 2, group: 'plats_prepares' },
  { nom: 'Sandwich', categorie: 'plats_prepares', unite: 'pieces', seuil: 2, group: 'plats_prepares' },
  { nom: 'Tacos', categorie: 'plats_prepares', unite: 'pieces', seuil: 2, group: 'plats_prepares' },
  { nom: 'Burritos', categorie: 'plats_prepares', unite: 'pieces', seuil: 2, group: 'plats_prepares' },
  { nom: 'Tamales', categorie: 'plats_prepares', unite: 'pieces', seuil: 2, group: 'plats_prepares' },
  { nom: 'Falafels', categorie: 'plats_prepares', unite: 'pieces', seuil: 6, group: 'plats_prepares' },
  { nom: 'Omelette', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  { nom: 'Plat mijoté', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  { nom: 'Fondue', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  { nom: 'Salade verte', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  { nom: 'Popcorn', categorie: 'alimentation', unite: 'pieces', seuil: 1, group: 'epicerie' },
  { nom: 'Sel', categorie: 'alimentation', unite: 'kg', seuil: 0.2, group: 'epicerie' },
  // Cuisine asiatique & plats du monde
  { nom: 'Crackers de riz', categorie: 'alimentation', unite: 'pieces', seuil: 1, group: 'epicerie' },
  { nom: 'Boulettes de riz', categorie: 'plats_prepares', unite: 'pieces', seuil: 4, group: 'plats_prepares' },
  { nom: 'Curry riz', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  { nom: 'Nouilles', categorie: 'alimentation', unite: 'kg', seuil: 0.4, group: 'epicerie' },
  { nom: 'Oden', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  { nom: 'Fish cake', categorie: 'viande_poisson', unite: 'pieces', seuil: 2, group: 'viande_poisson' },
  { nom: 'Moon cake', categorie: 'desserts', unite: 'pieces', seuil: 2, group: 'desserts' },
  { nom: 'Dango', categorie: 'desserts', unite: 'pieces', seuil: 3, group: 'desserts' },
  { nom: 'Raviolis', categorie: 'plats_prepares', unite: 'pieces', seuil: 8, group: 'plats_prepares' },
  { nom: 'Fortune cookies', categorie: 'desserts', unite: 'pieces', seuil: 4, group: 'desserts' },
  { nom: 'Takeout box', categorie: 'plats_prepares', unite: 'pieces', seuil: 1, group: 'plats_prepares' },
  // Desserts & sucreries
  { nom: 'Glace italienne', categorie: 'desserts', unite: 'pieces', seuil: 2, group: 'desserts' },
  { nom: 'Glace pilée', categorie: 'desserts', unite: 'pieces', seuil: 2, group: 'desserts' },
  { nom: 'Crème glacée', categorie: 'desserts', unite: 'pieces', seuil: 2, group: 'desserts' },
  { nom: 'Donuts', categorie: 'desserts', unite: 'pieces', seuil: 4, group: 'desserts' },
  { nom: 'Cookies', categorie: 'desserts', unite: 'pieces', seuil: 6, group: 'desserts' },
  { nom: 'Gâteau anniversaire', categorie: 'desserts', unite: 'pieces', seuil: 1, group: 'desserts' },
  { nom: 'Part de gâteau', categorie: 'desserts', unite: 'pieces', seuil: 2, group: 'desserts' },
  { nom: 'Cupcakes', categorie: 'desserts', unite: 'pieces', seuil: 4, group: 'desserts' },
  { nom: 'Tarte', categorie: 'desserts', unite: 'pieces', seuil: 1, group: 'desserts' },
  { nom: 'Chocolat', categorie: 'desserts', unite: 'pieces', seuil: 2, group: 'desserts' },
  { nom: 'Bonbons', categorie: 'desserts', unite: 'pieces', seuil: 6, group: 'desserts' },
  { nom: 'Sucettes', categorie: 'desserts', unite: 'pieces', seuil: 6, group: 'desserts' },
  { nom: 'Crème dessert', categorie: 'desserts', unite: 'pieces', seuil: 2, group: 'desserts' },
  { nom: 'Miel', categorie: 'desserts', unite: 'pieces', seuil: 1, group: 'desserts' },
  { nom: 'Lait', categorie: 'laitier', unite: 'L', seuil: 2, group: 'epicerie' },
  { nom: 'Café', categorie: 'boisson', unite: 'kg', seuil: 0.2, group: 'epicerie' },
  { nom: 'Eau', categorie: 'boisson', unite: 'L', seuil: 5, group: 'epicerie' },
  { nom: 'Biberon', categorie: 'boisson', unite: 'pieces', seuil: 1, group: 'boissons' },
  { nom: 'Thé vert', categorie: 'boisson', unite: 'g', seuil: 50, group: 'boissons' },
  { nom: 'Soda', categorie: 'boisson', unite: 'L', seuil: 1, group: 'boissons' },
  { nom: 'Bubble tea', categorie: 'boisson', unite: 'pieces', seuil: 2, group: 'boissons' },
  { nom: 'Saké', categorie: 'boisson', unite: 'L', seuil: 1, group: 'boissons' },
  { nom: 'Bières', categorie: 'boisson', unite: 'pieces', seuil: 4, group: 'boissons' },
  { nom: 'Champagne', categorie: 'boisson', unite: 'pieces', seuil: 1, group: 'boissons' },
  { nom: 'Whisky', categorie: 'boisson', unite: 'L', seuil: 1, group: 'boissons' },
  { nom: 'Cocktail', categorie: 'boisson', unite: 'pieces', seuil: 2, group: 'boissons' },
  { nom: 'Jus tropical', categorie: 'boisson', unite: 'L', seuil: 1, group: 'boissons' },
  { nom: 'Maté', categorie: 'boisson', unite: 'g', seuil: 50, group: 'boissons' },
  { nom: 'Œufs', categorie: 'laitier', unite: 'pièces', seuil: 6, group: 'epicerie' },
  { nom: 'Fromage', categorie: 'laitier', unite: 'kg', seuil: 0.5, group: 'epicerie' },
  { nom: 'Beurre', categorie: 'laitier', unite: 'kg', seuil: 0.2, group: 'epicerie' },
  { nom: 'Yaourt', categorie: 'laitier', unite: 'pièces', seuil: 4, group: 'epicerie' },
  { nom: 'Sucre', categorie: 'alimentation', unite: 'kg', seuil: 0.5, group: 'epicerie' },
  { nom: 'Farine', categorie: 'alimentation', unite: 'kg', seuil: 0.5, group: 'epicerie' },
  { nom: 'Huile', categorie: 'alimentation', unite: 'L', seuil: 0.5, group: 'epicerie' },
  { nom: 'Pâtes', categorie: 'alimentation', unite: 'kg', seuil: 0.5, group: 'epicerie' },
  { nom: 'Riz', categorie: 'alimentation', unite: 'kg', seuil: 0.5, group: 'epicerie' },
  { nom: 'Céréales', categorie: 'alimentation', unite: 'kg', seuil: 0.3, group: 'epicerie' },
  { nom: "Jus d'orange", categorie: 'boisson', unite: 'L', seuil: 1, group: 'epicerie' },
  { nom: 'Thé', categorie: 'boisson', unite: 'g', seuil: 50, group: 'epicerie' },
  { nom: 'Confiture', categorie: 'alimentation', unite: 'pièces', seuil: 1, group: 'epicerie' },
  { nom: 'Conserves tomates', categorie: 'conserve', unite: 'pièces', seuil: 2, group: 'epicerie' },
  { nom: 'Thon en boîte', categorie: 'conserve', unite: 'pièces', seuil: 2, group: 'epicerie' },
  // Hygiène & Nettoyage
  { nom: 'Liquide vaisselle', categorie: 'entretien', unite: 'L', seuil: 0.5, group: 'nettoyage' },
  { nom: 'Lessive', categorie: 'entretien', unite: 'kg', seuil: 0.5, group: 'nettoyage' },
  { nom: 'Papier toilette', categorie: 'hygiene', unite: 'pièces', seuil: 4, group: 'nettoyage' },
  { nom: 'Essuie-tout', categorie: 'hygiene', unite: 'pièces', seuil: 2, group: 'nettoyage' },
  { nom: 'Gel douche', categorie: 'hygiene', unite: 'L', seuil: 0.2, group: 'nettoyage' },
  { nom: 'Shampooing', categorie: 'hygiene', unite: 'L', seuil: 0.2, group: 'nettoyage' },
  { nom: 'Dentifrice', categorie: 'hygiene', unite: 'pièces', seuil: 1, group: 'nettoyage' },
  { nom: 'Savon', categorie: 'hygiene', unite: 'pièces', seuil: 2, group: 'nettoyage' },
  { nom: 'Déodorant', categorie: 'hygiene', unite: 'pièces', seuil: 1, group: 'nettoyage' },
  { nom: 'Nettoyant multi-surfaces', categorie: 'entretien', unite: 'L', seuil: 0.5, group: 'nettoyage' },
  // Vêtements
  { nom: 'Chaussettes', categorie: 'vetement', unite: 'pièces', seuil: 3, group: 'vetement' },
  { nom: 'Sous-vêtements', categorie: 'vetement', unite: 'pièces', seuil: 5, group: 'vetement' },
  { nom: 'T-shirt', categorie: 'vetement', unite: 'pièces', seuil: 3, group: 'vetement' },
  { nom: 'Pantalon', categorie: 'vetement', unite: 'pièces', seuil: 2, group: 'vetement' },
  { nom: 'Pull / Sweat', categorie: 'vetement', unite: 'pièces', seuil: 2, group: 'vetement' },
];

const SUGGESTION_DISPLAY_GROUPS = [
  { id: 'fruits_legumes', label: '🥦 Fruits & Légumes' },
  { id: 'epicerie', label: '🛒 Épicerie & Courses' },
  { id: 'viande_poisson', label: '🍖 Viande & Poisson' },
  { id: 'plats_prepares', label: '🍔 Repas & Plats préparés' },
  { id: 'desserts', label: '🍰 Desserts & Sucreries' },
  { id: 'boissons', label: '🥤 Boissons' },
  { id: 'nettoyage', label: '🧹 Hygiène & Nettoyage' },
  { id: 'vetement', label: '👔 Vêtements' },
];

const getCategoryGroup = (categorie) => {
  const c = String(categorie || '').toLowerCase();
  if (c === 'fruits_legumes') return 'fruits_legumes';
  if (c === 'vetement') return 'vetement';
  if (c === 'viande_poisson') return 'viande_poisson';
  if (c === 'plats_prepares') return 'plats_prepares';
  if (c === 'desserts') return 'desserts';
  if (c === 'boisson') return 'boissons';
  if (['hygiene', 'entretien'].includes(c)) return 'nettoyage';
  return 'epicerie';
};

const normalizeSuggestionMatcher = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/['’]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const DASHBOARD_TABS = [
  { value: 'achats', labelKey: 'tabs.shopping', icon: ShoppingCart, description: 'Liste d achats, suggestions et lien direct avec le stock.' },
  { value: 'stock', labelKey: 'tabs.stock', icon: Package, description: 'Etat actuel du foyer, quantites et alertes.' },
  { value: 'budget', labelKey: 'tabs.budget', icon: CreditCard, testId: 'budget-tab', description: 'Depenses du mois, budgets par categorie et alertes.' },
  { value: 'historique', labelKey: 'tabs.history', icon: Calendar, description: 'Historique des achats et mouvements du foyer.' },
  { value: 'ia', labelKey: 'tabs.ai', icon: Brain, description: 'Assistant intelligent et recommandations de consommation.' },
  { value: 'analytics', label: 'Analytics', icon: BarChart3, testId: 'analytics-tab', description: 'Vue globale des tendances et performances du stock.' },
  { value: 'apprentissage', labelKey: 'tabs.learning', icon: Check, description: 'Habitudes apprises et references de consommation.' },
  { value: 'foyer', labelKey: 'household.title', icon: Users, description: 'Gestion des membres et articles partages.' },
  { value: 'support', labelKey: 'tabs.support', icon: Mail, description: 'Support et retours produit.' },
  { value: 'about', labelKey: 'tabs.about', icon: Info, description: 'Presentation de SmartInventory et onboarding.' },
  { value: 'abonnement', labelKey: 'subscription.title', icon: Crown, description: 'Gestion de votre abonnement et reactivation.' },
];

const DASHBOARD_THEMES = {
  beige: {
    label: 'Beige',
    shellBackground: 'linear-gradient(135deg, #f7f1e3 0%, #efe1c7 45%, #ead7c2 100%)',
    panelBackground: 'rgba(255, 252, 247, 0.88)',
    heroBackground: 'linear-gradient(135deg, rgba(255,250,240,0.95), rgba(239,225,199,0.92))',
    sidebarBackground: 'linear-gradient(180deg, #6f5b47 0%, #4f4033 100%)',
    bottomBarBackground: 'rgba(79, 64, 51, 0.92)',
    accent: '#a16207',
    accentSoft: '#fde68a',
    accentStrong: '#854d0e',
    surfaceBorder: 'rgba(161, 98, 7, 0.18)',
    surfaceText: '#31251a',
    mutedText: '#6b5b4d',
    preview: ['#f7f1e3', '#d6b98c', '#6f5b47'],
  },
  white: {
    label: 'White',
    shellBackground: 'linear-gradient(135deg, #ffffff 0%, #f3f7fb 55%, #e8eef7 100%)',
    panelBackground: 'rgba(255, 255, 255, 0.92)',
    heroBackground: 'linear-gradient(135deg, rgba(255,255,255,0.96), rgba(240,247,255,0.94))',
    sidebarBackground: 'linear-gradient(180deg, #1f2937 0%, #111827 100%)',
    bottomBarBackground: 'rgba(17, 24, 39, 0.9)',
    accent: '#2563eb',
    accentSoft: '#bfdbfe',
    accentStrong: '#1d4ed8',
    surfaceBorder: 'rgba(37, 99, 235, 0.14)',
    surfaceText: '#111827',
    mutedText: '#4b5563',
    preview: ['#ffffff', '#dbeafe', '#1f2937'],
  },
  rose: {
    label: 'Rose',
    shellBackground: 'linear-gradient(135deg, #fff1f5 0%, #ffe4ec 45%, #ffd3e0 100%)',
    panelBackground: 'rgba(255, 248, 251, 0.9)',
    heroBackground: 'linear-gradient(135deg, rgba(255,245,248,0.96), rgba(255,222,235,0.92))',
    sidebarBackground: 'linear-gradient(180deg, #9f1239 0%, #881337 100%)',
    bottomBarBackground: 'rgba(136, 19, 55, 0.92)',
    accent: '#e11d48',
    accentSoft: '#fecdd3',
    accentStrong: '#be123c',
    surfaceBorder: 'rgba(225, 29, 72, 0.18)',
    surfaceText: '#4c0519',
    mutedText: '#831843',
    preview: ['#fff1f5', '#fda4af', '#881337'],
  },
  aubergine: {
    label: 'Aubergine',
    shellBackground: 'linear-gradient(135deg, #f5f3ff 0%, #ece7ff 45%, #ddd6fe 100%)',
    panelBackground: 'rgba(249, 247, 255, 0.88)',
    heroBackground: 'linear-gradient(135deg, rgba(248,245,255,0.96), rgba(226,219,255,0.92))',
    sidebarBackground: 'linear-gradient(180deg, #4c1d95 0%, #312e81 100%)',
    bottomBarBackground: 'rgba(49, 46, 129, 0.92)',
    accent: '#7c3aed',
    accentSoft: '#ddd6fe',
    accentStrong: '#5b21b6',
    surfaceBorder: 'rgba(124, 58, 237, 0.18)',
    surfaceText: '#2e1065',
    mutedText: '#5b21b6',
    preview: ['#f5f3ff', '#c4b5fd', '#312e81'],
  },
  emerald: {
    label: 'Vert actuel',
    shellBackground: 'linear-gradient(135deg, #ecfdf5 0%, #cffafe 45%, #ccfbf1 100%)',
    panelBackground: 'rgba(255, 255, 255, 0.86)',
    heroBackground: 'linear-gradient(135deg, rgba(236,253,245,0.96), rgba(204,251,241,0.92))',
    sidebarBackground: 'linear-gradient(180deg, #065f46 0%, #0f766e 100%)',
    bottomBarBackground: 'rgba(6, 95, 70, 0.92)',
    accent: '#059669',
    accentSoft: '#a7f3d0',
    accentStrong: '#047857',
    surfaceBorder: 'rgba(5, 150, 105, 0.16)',
    surfaceText: '#052e2b',
    mutedText: '#0f766e',
    preview: ['#ecfdf5', '#6ee7b7', '#065f46'],
  },
  dark: {
    label: 'Sombre',
    shellBackground: 'linear-gradient(135deg, #020617 0%, #111827 55%, #1e293b 100%)',
    panelBackground: 'rgba(15, 23, 42, 0.82)',
    heroBackground: 'linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.92))',
    sidebarBackground: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)',
    bottomBarBackground: 'rgba(2, 6, 23, 0.96)',
    accent: '#38bdf8',
    accentSoft: '#0f172a',
    accentStrong: '#0ea5e9',
    surfaceBorder: 'rgba(56, 189, 248, 0.18)',
    surfaceText: '#e2e8f0',
    mutedText: '#94a3b8',
    preview: ['#020617', '#0ea5e9', '#e2e8f0'],
  },
};

const formatCurrencyValue = (value) => `${Number(value || 0).toFixed(2)} €`;

const getBudgetAlertTone = (ratio) => {
  if (ratio >= 1) return 'text-red-600';
  if (ratio >= 0.85) return 'text-orange-500';
  return 'text-emerald-600';
};

const DAY_IN_MS = 1000 * 60 * 60 * 24;

const STOCK_TYPE_LABELS = {
  consommable: 'Consommable',
  occasionnel: 'Occasionnel',
  one_shot: 'One shot',
};

const STOCK_STATUS_LABELS = {
  faible: 'Faible',
  moyen: 'Moyen',
  ok: 'OK',
};

const CONSUMABLE_CATEGORIES = new Set([
  'alimentation',
  'boisson',
  'laitier',
  'boulangerie',
  'hygiene',
  'entretien',
  'fruits_legumes',
]);

const OCCASIONAL_CATEGORIES = new Set(['conserve']);
const ONE_SHOT_CATEGORIES = new Set(['vetement', 'electronique', 'hightech', 'meuble']);

const normalizeStockName = (value) => String(value || '').trim().toLowerCase();

const getDaysSince = (timestamp) => {
  if (!timestamp) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((Date.now() - timestamp) / DAY_IN_MS));
};

const inferStockType = ({ category, purchaseCount, daysSincePurchase }) => {
  if (ONE_SHOT_CATEGORIES.has(category)) return 'one_shot';
  if (purchaseCount <= 1 && Number.isFinite(daysSincePurchase) && daysSincePurchase >= 14) return 'one_shot';
  if (CONSUMABLE_CATEGORIES.has(category)) return 'consommable';
  if (OCCASIONAL_CATEGORIES.has(category)) return 'occasionnel';
  if (purchaseCount >= 4) return 'consommable';
  return 'occasionnel';
};

const inferStockStatus = ({ isAlert, hasQuantity, quantity, threshold, estimatedDaysRemaining }) => {
  if (!hasQuantity) {
    return 'moyen';
  }
  if (Number.isFinite(estimatedDaysRemaining)) {
    if (estimatedDaysRemaining > 4) {
      return 'ok';
    }
    if (estimatedDaysRemaining <= 2) {
      return 'faible';
    }
    return 'moyen';
  }
  if (isAlert || quantity <= threshold) {
    return 'faible';
  }
  if (quantity <= threshold * 1.5) {
    return 'moyen';
  }
  return 'ok';
};

function ParticulierDashboard() {
  const { user, logout, refreshUser } = useContext(AuthContext);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [stockStatus, setStockStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showMovement, setShowMovement] = useState(false);
  const [showQuantityPopup, setShowQuantityPopup] = useState(false);
  const [selectedItemForPurchase, setSelectedItemForPurchase] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState('');
  const [aiEstimations, setAiEstimations] = useState({});
  const [learningPatterns, setLearningPatterns] = useState([]);
  const [showEditItem, setShowEditItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editProfileData, setEditProfileData] = useState({ 
    nb_personnes: '', 
    report_days: ['mercredi', 'vendredi'],
    report_time: '12:00' 
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, itemId: null, itemName: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReceiptIntroPopup, setShowReceiptIntroPopup] = useState(false);
  const [showReceiptScanner, setShowReceiptScanner] = useState(false);
  const [showBarcodePermissionPopup, setShowBarcodePermissionPopup] = useState(false);
  const [cameraPermissionChoice, setCameraPermissionChoice] = useState(null); // 'once' | 'always' | null
  const [notificationRefreshToken, setNotificationRefreshToken] = useState(0);
  const [editingQuantityId, setEditingQuantityId] = useState(null);
  const [tempQuantity, setTempQuantity] = useState('');
  const [tempPrice, setTempPrice] = useState('');
  const [stockCategoryFilters, setStockCategoryFilters] = useState([]);
  const [stockStatusFilters, setStockStatusFilters] = useState([]);
  const [stockTypeFilters, setStockTypeFilters] = useState([]);
  const [showStockOkSection, setShowStockOkSection] = useState(false);
  const [showArchiveSection, setShowArchiveSection] = useState(false);
  const [showPrixMagasinModal, setShowPrixMagasinModal] = useState(false);
  const [prixMagasinProductInfo, setPrixMagasinProductInfo] = useState(null);
  const [prixMagasinDefaultPrice, setPrixMagasinDefaultPrice] = useState(null);
  const [pendingPurchaseMovementId, setPendingPurchaseMovementId] = useState(null);
  const [hiddenShoppingItemIds, setHiddenShoppingItemIds] = useState([]);
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [estimationProductInfo, setEstimationProductInfo] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedArticleForConfig, setSelectedArticleForConfig] = useState(null);
  const [paywallActive, setPaywallActive] = useState(false);
  const [paywallMessage, setPaywallMessage] = useState('');
  const [gracePeriod, setGracePeriod] = useState({ active: false, daysLeft: 0, deletionDate: null, showPopup: false });
  const [alternativePriceSuggestions, setAlternativePriceSuggestions] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [newItem, setNewItem] = useState({
    nom: '',
    categorie: 'alimentation',
    unite: 'kg',
    seuil_alerte: '',
    initial_type: 'ACHAT',
    initial_quantite: '',
    initial_prix: '',
    initial_magasin: '',
    perissable: false,
    dluo_dlc: '',
    consommation_estimee: '',
    consommation_unite: ''
  });
  const [addItemContext, setAddItemContext] = useState('manual');
  const [articleKnown, setArticleKnown] = useState(null);
  const [showConsumptionInput, setShowConsumptionInput] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [barcodeStartManualMode, setBarcodeStartManualMode] = useState(false);
  const [educationSheet, setEducationSheet] = useState(null);
  const [showSuggestionsPopup, setShowSuggestionsPopup] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [suggestionQueue, setSuggestionQueue] = useState([]);
  const [showBatchArticlesModal, setShowBatchArticlesModal] = useState(false);
  const [batchArticlesConfig, setBatchArticlesConfig] = useState([]);
  const [shoppingPlannerActiveList, setShoppingPlannerActiveList] = useState('suggestions');
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [dashboardTheme, setDashboardTheme] = useState(() => {
    try {
      return localStorage.getItem('smartinventory_dashboard_theme') || 'emerald';
    } catch {
      return 'emerald';
    }
  });
  const [purchaseTransition, setPurchaseTransition] = useState(null);
  const [customCategories, setCustomCategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem('customItemCategories') || '[]'); } catch { return []; }
  });
  const [catInputFor, setCatInputFor] = useState(null); // 'new' | 'edit' | null
  const [catInputValue, setCatInputValue] = useState('');
  
  const [newMovement, setNewMovement] = useState({
    item_id: '',
    type: 'ACHAT',
    quantite: '',
    source: '',
    note: ''
  });
  const isHistoryPopRef = useRef(false);
  const hasLoadedDashboardRef = useRef(false);

  // Articles suggérés pour particuliers
  const suggestedItems = [
    { nom: 'Huile d\'olive', categorie: 'alimentation', unite: 'L', seuil: 0.5 },
    { nom: 'Sucre', categorie: 'alimentation', unite: 'kg', seuil: 1 },
    { nom: 'Café', categorie: 'boisson', unite: 'kg', seuil: 0.2 },
    { nom: 'Lait', categorie: 'laitier', unite: 'L', seuil: 2 },
    { nom: 'Pain', categorie: 'boulangerie', unite: 'pièces', seuil: 2 },
    { nom: 'Œufs', categorie: 'laitier', unite: 'pièces', seuil: 6 },
    { nom: 'Eau', categorie: 'boisson', unite: 'L', seuil: 5 },
    { nom: 'Olives', categorie: 'conserve', unite: 'kg', seuil: 0.3 }
  ];

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const normalizeItemUnit = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return 'kg';
    if (['kg', 'kilo', 'kilos'].includes(normalized)) return 'kg';
    if (['g', 'gramme', 'grammes'].includes(normalized)) return 'g';
    if (['l', 'lt', 'litre', 'litres'].includes(normalized)) return 'L';
    if (['ml', 'millilitre', 'millilitres'].includes(normalized)) return 'ml';
    if (['piece', 'pieces', 'pièce', 'pièces', 'unite', 'unité', 'unites', 'unités'].includes(normalized)) return 'pièces';
    return 'kg';
  };

  const formatApiErrorDetail = (detail) => {
    if (!detail) return null;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((entry) => {
          if (typeof entry === 'string') return entry;
          if (entry?.msg) return entry.msg;
          return null;
        })
        .filter(Boolean);
      return messages.length ? messages.join(' | ') : 'Requete invalide';
    }
    if (typeof detail === 'object' && detail.msg) {
      return detail.msg;
    }
    return 'Requete invalide';
  };

  const openBarcodeFlow = ({ manualOnly = false } = {}) => {
    if (manualOnly) {
      setBarcodeStartManualMode(true);
      setShowBarcodePermissionPopup(false);
      setShowBarcodeScanner(true);
      return;
    }

    // Vérifier si l'utilisateur a déjà autorisé "toujours"
    try {
      const alwaysGranted = localStorage.getItem('barcode_camera_permission_always') === 'true';
      if (alwaysGranted) {
        // Passer directement à la demande de permission avec 'always'
        requestBarcodeCameraPermission('always');
        return;
      }
    } catch {}

    // Sinon, afficher le popup de permission
    setShowBarcodePermissionPopup(true);
  };

  const requestBarcodeCameraPermission = async (permissionType = 'once') => {
    setShowBarcodePermissionPopup(false);
    setCameraPermissionChoice(permissionType);

    // Sauvegarder le choix dans localStorage si 'always'
    if (permissionType === 'always') {
      try {
        localStorage.setItem('barcode_camera_permission_always', 'true');
      } catch {}
    }

    try {
      // getUserMedia déclenche la permission OS Android dans WebView Capacitor.
      if (!navigator?.mediaDevices?.getUserMedia) {
        setBarcodeStartManualMode(false);
        setShowBarcodeScanner(true);
        return;
      }

      const permissionConstraints = [
        { video: { facingMode: { exact: 'environment' } } },
        { video: { facingMode: 'environment' } },
        { video: true },
      ];

      let stream = null;
      let lastError = null;
      for (const constraints of permissionConstraints) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!stream) {
        throw lastError || new Error('CAMERA_PERMISSION_FAILED');
      }
      stream.getTracks().forEach((track) => track.stop());

      setBarcodeStartManualMode(false);
      setShowBarcodeScanner(true);
      toast.success('Permission caméra accordée');
    } catch (error) {
      const message = String(error?.message || error || '').toLowerCase();
      const denied = error?.name === 'NotAllowedError' || 
                     message.includes('permission') || 
                     message.includes('denied');

      if (denied) {
        toast.error('Permission caméra refusée. Vous pouvez réessayer ou utiliser la saisie manuelle sans passer par les réglages.');
      } else {
        toast.error('Caméra indisponible pour le moment. Passage en saisie manuelle.');
      }

      setBarcodeStartManualMode(true);
      setShowBarcodeScanner(true);
    }
  };

  const openFoodEducationSheet = (item) => {
    const name = item?.nom || item?.item_nom || item?.itemName || 'Aliment';
    const ciqualFood = findCiqualFoodByName(name);
    const sheet = getFoodEducationSheet(name, {
      ...(ciqualFood?.nutrition100g || {}),
      ...inferNutritionReference(item || {}),
    });
    setEducationSheet(sheet);
  };

  // Catalogue utilisé pour les suggestions — référence PREDEFINED_CATALOG défini en dehors du composant

  const buildRecommendedNewItemCatalog = () => {
    const normalizedItems = new Map();
    const recentScores = new Map();
    const recentThreshold = Date.now() - (1000 * 60 * 60 * 24 * 21);

    movements.forEach((movement) => {
      const movementType = String(movement.type || '').toUpperCase();
      const movementDate = new Date(movement.date || movement.created_at || 0).getTime() || 0;
      if (!movement.item_id || !movementDate) {
        return;
      }
      const existingScore = recentScores.get(movement.item_id) || {
        latestPurchaseAt: 0,
        latestActivityAt: 0,
        purchaseCount: 0,
        totalPurchasedQuantity: 0,
      };
      existingScore.latestActivityAt = Math.max(existingScore.latestActivityAt, movementDate);
      if (movementType === 'ACHAT') {
        existingScore.latestPurchaseAt = Math.max(existingScore.latestPurchaseAt, movementDate);
        existingScore.purchaseCount += 1;
        existingScore.totalPurchasedQuantity += Math.abs(parseNumericValue(movement.quantite) || 0);
      }
      recentScores.set(movement.item_id, existingScore);
    });

    items.forEach((item) => {
      const key = item.nom?.trim().toLowerCase();
      if (!key) {
        return;
      }

      const itemScore = recentScores.get(item.id) || { latestPurchaseAt: 0, latestActivityAt: 0 };
      const existing = normalizedItems.get(key);
      const candidate = {
        nom: item.nom,
        categorie: item.categorie || 'alimentation',
        unite: item.unite || 'kg',
        seuil: item.seuil_alerte || 1,
        latestPurchaseAt: itemScore.latestPurchaseAt,
        latestActivityAt: itemScore.latestActivityAt,
        purchaseCount: itemScore.purchaseCount,
        totalPurchasedQuantity: itemScore.totalPurchasedQuantity,
        source: 'history',
        group: getCategoryGroup(item.categorie)
      };

      if (!existing || candidate.latestPurchaseAt > existing.latestPurchaseAt || candidate.latestActivityAt > existing.latestActivityAt) {
        normalizedItems.set(key, candidate);
      }
    });

    PREDEFINED_CATALOG.forEach((item) => {
      const key = item.nom.trim().toLowerCase();
      if (!normalizedItems.has(key)) {
        normalizedItems.set(key, {
          ...item,
          latestPurchaseAt: 0,
          latestActivityAt: 0,
          purchaseCount: 0,
          totalPurchasedQuantity: 0,
          source: 'default'
        });
      }
    });

    return Array.from(normalizedItems.values()).sort((left, right) => {
      const rightRecent = right.latestPurchaseAt >= recentThreshold ? 1 : 0;
      const leftRecent = left.latestPurchaseAt >= recentThreshold ? 1 : 0;
      if (rightRecent !== leftRecent) {
        return rightRecent - leftRecent;
      }
      if (right.purchaseCount !== left.purchaseCount) {
        return right.purchaseCount - left.purchaseCount;
      }
      if (right.latestPurchaseAt !== left.latestPurchaseAt) {
        return right.latestPurchaseAt - left.latestPurchaseAt;
      }
      if (right.latestActivityAt !== left.latestActivityAt) {
        return right.latestActivityAt - left.latestActivityAt;
      }
      return left.nom.localeCompare(right.nom, 'fr', { sensitivity: 'base' });
    });
  };

  const recommendedNewItemCatalog = buildRecommendedNewItemCatalog();
  const prioritizedQuickAddItems = recommendedNewItemCatalog.slice(0, 9);
  const recentSuggestedItems = recommendedNewItemCatalog.filter((suggestion) => suggestion.source === 'history').slice(0, 6);
  const remainingSuggestedItems = recommendedNewItemCatalog.filter((suggestion) => !recentSuggestedItems.some((recent) => recent.nom === suggestion.nom));

  const getSuggestionEmoji = (input) => {
    const normalizedName = normalizeSuggestionMatcher(input?.nom || input?.item_nom || input || '');
    const normalizedCategory = normalizeSuggestionMatcher(input?.categorie || input || '');

    const nameEmojiRules = [
      { terms: ['pomme verte'], emoji: '🍏' },
      { terms: ['pomme'], emoji: '🍎' },
      { terms: ['poire'], emoji: '🍐' },
      { terms: ['orange'], emoji: '🍊' },
      { terms: ['citron'], emoji: '🍋' },
      { terms: ['banane'], emoji: '🍌' },
      { terms: ['pasteque'], emoji: '🍉' },
      { terms: ['raisin'], emoji: '🍇' },
      { terms: ['fraise'], emoji: '🍓' },
      { terms: ['myrtille', 'blueberr'], emoji: '🫐' },
      { terms: ['melon'], emoji: '🍈' },
      { terms: ['cerise'], emoji: '🍒' },
      { terms: ['peche'], emoji: '🍑' },
      { terms: ['mangue'], emoji: '🥭' },
      { terms: ['ananas'], emoji: '🍍' },
      { terms: ['noix de coco', 'coco'], emoji: '🥥' },
      { terms: ['kiwi'], emoji: '🥝' },
      { terms: ['tomate'], emoji: '🍅' },
      { terms: ['avocat'], emoji: '🥑' },
      { terms: ['aubergine'], emoji: '🍆' },
      { terms: ['pomme de terre'], emoji: '🥔' },
      { terms: ['carotte'], emoji: '🥕' },
      { terms: ['mais'], emoji: '🌽' },
      { terms: ['concombre'], emoji: '🥒' },
      { terms: ['salade', 'laitue', 'legume feuille'], emoji: '🥬' },
      { terms: ['brocoli'], emoji: '🥦' },
      { terms: ['ail'], emoji: '🧄' },
      { terms: ['oignon'], emoji: '🧅' },
      { terms: ['haricot', 'fayot', 'bean'], emoji: '🫘' },
      { terms: ['poivron'], emoji: '🫑' },
      { terms: ['piment', 'hot pepper'], emoji: '🌶️' },
      { terms: ['olive', 'huile'], emoji: '🫒' },
      { terms: ['pain de mie', 'bread'], emoji: '🍞' },
      { terms: ['croissant'], emoji: '🥐' },
      { terms: ['pain', 'baguette', 'boulangerie'], emoji: '🥖' },
      { terms: ['pain plat', 'flatbread'], emoji: '🫓' },
      { terms: ['bretzel', 'pretzel'], emoji: '🥨' },
      { terms: ['bagel'], emoji: '🥯' },
      { terms: ['pancake', 'crepe epaisse'], emoji: '🥞' },
      { terms: ['gaufre', 'waffle'], emoji: '🧇' },
      { terms: ['fromage', 'cheese'], emoji: '🧀' },
      { terms: ['viande avec os', 'meat on bone'], emoji: '🍖' },
      { terms: ['poulet', 'cuisse de poulet', 'poultry'], emoji: '🍗' },
      { terms: ['steak', 'viande', 'cut of meat'], emoji: '🥩' },
      { terms: ['crevette', 'shrimp'], emoji: '🍤' },
      { terms: ['sushi'], emoji: '🍣' },
      { terms: ['plateau repas', 'bento'], emoji: '🍱' },
      { terms: ['huitre', 'oyster'], emoji: '🦪' },
      { terms: ['hamburger', 'burger'], emoji: '🍔' },
      { terms: ['frite', 'french fries'], emoji: '🍟' },
      { terms: ['pizza'], emoji: '🍕' },
      { terms: ['hot-dog', 'hot dog'], emoji: '🌭' },
      { terms: ['sandwich'], emoji: '🥪' },
      { terms: ['taco'], emoji: '🌮' },
      { terms: ['burrito'], emoji: '🌯' },
      { terms: ['tamale'], emoji: '🫔' },
      { terms: ['falafel'], emoji: '🧆' },
      { terms: ['omelette', 'cooking'], emoji: '🍳' },
      { terms: ['plat mijote', 'shallow pan food'], emoji: '🥘' },
      { terms: ['pot of food', 'ragout'], emoji: '🍲' },
      { terms: ['fondue'], emoji: '🫕' },
      { terms: ['salade verte', 'green salad'], emoji: '🥗' },
      { terms: ['popcorn'], emoji: '🍿' },
      { terms: ['beurre', 'butter'], emoji: '🧈' },
      { terms: ['sel', 'salt'], emoji: '🧂' },
      { terms: ['conserve', 'canned food'], emoji: '🥫' },
      { terms: ['cracker de riz', 'rice cracker'], emoji: '🍘' },
      { terms: ['boulette de riz', 'rice ball'], emoji: '🍙' },
      { terms: ['fruit'], emoji: '🍎' },
      { terms: ['legume'], emoji: '🥬' },
      { terms: ['lait'], emoji: '🥛' },
      { terms: ['oeuf'], emoji: '🥚' },
      { terms: ['curry riz', 'curry rice'], emoji: '🍛' },
      { terms: ['nouille', 'ramen', 'steaming bowl'], emoji: '🍜' },
      { terms: ['riz'], emoji: '🍚' },
      { terms: ['pate'], emoji: '🍝' },
      { terms: ['oden'], emoji: '🍢' },
      { terms: ['fish cake'], emoji: '🍥' },
      { terms: ['moon cake'], emoji: '🥮' },
      { terms: ['dango'], emoji: '🍡' },
      { terms: ['ravioli', 'dumpling'], emoji: '🥟' },
      { terms: ['fortune cookie'], emoji: '🥠' },
      { terms: ['takeout box'], emoji: '🥡' },
      { terms: ['glace italienne', 'soft ice cream'], emoji: '🍦' },
      { terms: ['glace pilee', 'shaved ice'], emoji: '🍧' },
      { terms: ['creme glacee', 'ice cream'], emoji: '🍨' },
      { terms: ['donut', 'doughnut'], emoji: '🍩' },
      { terms: ['cookie'], emoji: '🍪' },
      { terms: ['gateau anniversaire', 'birthday cake'], emoji: '🎂' },
      { terms: ['part de gateau', 'shortcake'], emoji: '🍰' },
      { terms: ['cupcake'], emoji: '🧁' },
      { terms: ['tarte', 'pie'], emoji: '🥧' },
      { terms: ['chocolat', 'chocolate'], emoji: '🍫' },
      { terms: ['bonbon', 'candy'], emoji: '🍬' },
      { terms: ['sucette', 'lollipop'], emoji: '🍭' },
      { terms: ['creme dessert', 'custard'], emoji: '🍮' },
      { terms: ['miel', 'honey'], emoji: '🍯' },
      { terms: ['biberon', 'baby bottle'], emoji: '🍼' },
      { terms: ['thon', 'poisson', 'sardine', 'saumon'], emoji: '🐟' },
      { terms: ['cafe'], emoji: '☕' },
      { terms: ['the', 'teacup'], emoji: '🍵' },
      { terms: ['eau'], emoji: '💧' },
      { terms: ['jus'], emoji: '🧃' },
      { terms: ['soda', 'cup with straw'], emoji: '🥤' },
      { terms: ['bubble tea'], emoji: '🧋' },
      { terms: ['sake', 'saké'], emoji: '🍶' },
      { terms: ['biere', 'beer'], emoji: '🍻' },
      { terms: ['champagne', 'clinking glasses'], emoji: '🥂' },
      { terms: ['whisky', 'tumbler glass'], emoji: '🥃' },
      { terms: ['cocktail'], emoji: '🍸' },
      { terms: ['jus tropical', 'tropical drink'], emoji: '🍹' },
      { terms: ['mate'], emoji: '🧉' },
      { terms: ['sucre'], emoji: '🍬' },
      { terms: ['huile'], emoji: '🫒' },
      { terms: ['papier toilette'], emoji: '🧻' },
      { terms: ['savon', 'douche', 'shampooing', 'dentifrice'], emoji: '🧴' },
      { terms: ['lessive', 'nettoyant', 'javel', 'detergent'], emoji: '🧽' },
      { terms: ['chaussette', 't-shirt', 'pantalon', 'vetement'], emoji: '👕' },
    ];

    const byName = nameEmojiRules.find((rule) => rule.terms.some((term) => normalizedName.includes(term)));
    if (byName) return byName.emoji;

    if (normalizedCategory === 'fruits_legumes') return '🥦';
    if (normalizedCategory === 'vetement') return '👔';
    if (normalizedCategory === 'boisson') return '🥤';
    if (normalizedCategory === 'laitier') return '🧀';
    if (normalizedCategory === 'boulangerie') return '🥖';
    if (normalizedCategory === 'viande_poisson') return '🍖';
    if (normalizedCategory === 'plats_prepares') return '🍔';
    if (normalizedCategory === 'desserts') return '🍰';
    if (normalizedCategory === 'conserve') return '🥫';
    if (normalizedCategory === 'hygiene') return '🧴';
    if (normalizedCategory === 'entretien') return '🧽';
    return '🛒';
  };

  useEffect(() => {
    checkSubscriptionAccess();
    loadData();
    
    // Forcer le changement de mot de passe si temporaire
    if (user?.is_temporary_password || user?.must_change_password) {
      toast.warning('Vous devez changer votre mot de passe temporaire');
      setTimeout(() => {
        setShowChangePassword(true);
      }, 2000);
    }
  }, [user]);

  useEffect(() => {
    const handlePopState = (event) => {
      const requestedTab = event?.state?.dashboardTab || 'overview';
      isHistoryPopRef.current = true;
      setActiveTab(requestedTab);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const currentTabInHistory = window.history.state?.dashboardTab;
    if (isHistoryPopRef.current) {
      isHistoryPopRef.current = false;
      return;
    }
    if (currentTabInHistory === activeTab) {
      return;
    }

    const nextState = { ...(window.history.state || {}), dashboardTab: activeTab };
    if (currentTabInHistory === undefined) {
      window.history.replaceState(nextState, '');
    } else {
      window.history.pushState(nextState, '');
    }
  }, [activeTab]);

  useEffect(() => {
    try {
      localStorage.setItem('smartinventory_dashboard_theme', dashboardTheme);
    } catch {}
  }, [dashboardTheme]);

  useEffect(() => {
    if (!purchaseTransition) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setPurchaseTransition(null), 4500);
    return () => window.clearTimeout(timeoutId);
  }, [purchaseTransition]);

  const checkSubscriptionAccess = async () => {
    try {
      const res = await axios.get('/subscription/check-access');
      if (res.data.forced_logout) {
        toast.error(res.data.message || 'Votre essai gratuit a expire. Compte supprime.');
        logout();
        return;
      }
      if (res.data.access === false) {
        setPaywallActive(true);
        setPaywallMessage(res.data.message || 'Abonnement requis pour acceder a cette section.');
        return;
      }
      // Handle grace period
      if (res.data.reason === 'cancelled_grace_period') {
        setGracePeriod({
          active: true,
          daysLeft: res.data.grace_days_left || 0,
          deletionDate: res.data.deletion_scheduled_at,
          showPopup: res.data.show_popup || false
        });
        // Don't redirect - user can still use the app, just show warning banner
      }
    } catch (error) {
      // 401 handled by axios interceptor
    }
  };

  const loadData = async () => {
    try {
      if (!hasLoadedDashboardRef.current) {
        setLoading(true);
      }
      const [itemsRes, movementsRes, stockRes, patternsRes] = await Promise.all([
        axios.get('/items'),
        axios.get('/movements'), 
        axios.get('/stock'),
        axios.get('/learning/patterns')
      ]);
      
      setItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
      setMovements(Array.isArray(movementsRes.data) ? movementsRes.data : []);
      setStockStatus(Array.isArray(stockRes.data) ? stockRes.data : []);
      setLearningPatterns(patternsRes.data?.patterns || []);
      try {
        const shoppingStatusRes = await axios.get('/items/shopping-list/status');
        const hiddenIds = Array.isArray(shoppingStatusRes.data?.hidden_item_ids)
          ? shoppingStatusRes.data.hidden_item_ids
          : [];
        setHiddenShoppingItemIds(hiddenIds);
      } catch {
        setHiddenShoppingItemIds([]);
      }
      try {
        const budgetRes = await axios.get('/budget/monthly-summary');
        setBudgetSummary(budgetRes.data || null);
      } catch {
        setBudgetSummary(null);
      }
      try {
        const priceSuggestionsRes = await axios.get('/items/cheaper-alternatives');
        const nextSuggestions = Array.isArray(priceSuggestionsRes.data)
          ? Object.fromEntries(priceSuggestionsRes.data.map((suggestion) => [suggestion.item_id, suggestion]))
          : {};
        setAlternativePriceSuggestions(nextSuggestions);
      } catch (priceError) {
        setAlternativePriceSuggestions({});
      }
      setNotificationRefreshToken((prev) => prev + 1);
      setPaywallActive(false);
    } catch (error) {
      // Detect paywall 403
      if (error.response?.status === 403 && error.response?.data?.detail?.startsWith('PAYWALL:')) {
        setPaywallActive(true);
        setPaywallMessage(error.response.data.detail.replace('PAYWALL:', ''));
        setActiveTab('abonnement');
        return;
      }
      console.error('Erreur loadData:', error.response?.data || error.message || error);
      toast.error('Erreur lors du chargement des données. Les données précédentes sont conservées.');
    } finally {
      hasLoadedDashboardRef.current = true;
      setLoading(false);
    }
  };

  const resetNewItemForm = () => {
    setNewItem({
      nom: '',
      categorie: 'alimentation',
      unite: 'kg',
      seuil_alerte: '',
      initial_type: 'ACHAT',
      initial_quantite: '',
      initial_prix: '',
      initial_magasin: '',
      perissable: false,
      dluo_dlc: '',
      consommation_estimee: '',
      consommation_unite: ''
    });
    setAddItemContext('manual');
    setArticleKnown(null);
    setShowConsumptionInput(false);
  };

  const confirmCustomCategory = (forForm) => {
    const trimmed = catInputValue.trim().toLowerCase();
    if (!trimmed) return;
    const isPredefined = PREDEFINED_ITEM_CATEGORIES.some(c => c.value === trimmed);
    if (!isPredefined && !customCategories.includes(trimmed)) {
      const updated = [...customCategories, trimmed];
      setCustomCategories(updated);
      localStorage.setItem('customItemCategories', JSON.stringify(updated));
    }
    if (forForm === 'new') setNewItem((prev) => ({ ...prev, categorie: trimmed }));
    if (forForm === 'edit') setEditingItem((prev) => ({ ...prev, categorie: trimmed }));
    setCatInputFor(null);
    setCatInputValue('');
  };

  const applyQuickAddSuggestion = (suggestion) => {
    setNewItem((prev) => ({
      ...prev,
      nom: suggestion.nom,
      categorie: suggestion.categorie,
      unite: suggestion.unite,
      seuil_alerte: String(suggestion.seuil)
    }));
    setArticleKnown(null);
    setShowConsumptionInput(false);
  };

  const addSuggestionDirectlyToBuyList = async (suggestion) => {
    if (!suggestion?.nom) {
      toast.error('Suggestion invalide');
      return;
    }

    try {
      const normalizedThreshold = parseNumericValue(suggestion.seuil);
      const thresholdValue = Number.isFinite(normalizedThreshold) && normalizedThreshold >= 0
        ? normalizedThreshold
        : 1;

      const itemData = {
        nom: suggestion.nom,
        categorie: suggestion.categorie || 'alimentation',
        unite: normalizeItemUnit(suggestion.unite),
        secteur: 'PARTICULIER',
        seuil_alerte: thresholdValue,
        perissable: false,
        dluo_dlc: null
      };

      await axios.post('/items', itemData);
      toast.success('Article ajoute a la liste a acheter');
      await loadData();
      setShowSuggestionsPopup(false);
      setSelectedSuggestions(new Set());
    } catch (error) {
      console.error('Erreur lors de l\'ajout direct depuis suggestion:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Erreur lors de l\'ajout de la suggestion');
    }
  };

  const handleAddSuggestionsSelected = () => {
    const selectedArr = Array.from(selectedSuggestions);
    if (selectedArr.length === 0) return;
    
    const configItems = selectedArr.map(nomSelected => {
      const suggestion = recommendedNewItemCatalog.find(s => s.nom === nomSelected);
      if (!suggestion) return null;
      return {
        nom: suggestion.nom,
        categorie: suggestion.categorie,
        unite: suggestion.unite,
        initial_quantite: '1',
        initial_prix: '',
        initial_magasin: '',
        seuil_alerte: String(suggestion.seuil || 1)
      };
    }).filter(Boolean);
    
    setBatchArticlesConfig(configItems);
    setShowBatchArticlesModal(true);
    setShowSuggestionsPopup(false);
    setSelectedSuggestions(new Set());
  };

  const toggleSuggestionSelection = (suggestion) => {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(suggestion.nom)) {
        next.delete(suggestion.nom);
      } else {
        next.add(suggestion.nom);
      }
      return next;
    });
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const initialQuantity = parseNumericValue(newItem.initial_quantite);
      const initialPrice = parseNumericValue(newItem.initial_prix);
      const initialType = newItem.initial_type || 'ACHAT';
      const normalizedThreshold = parseNumericValue(newItem.seuil_alerte);
      const thresholdValue = Number.isFinite(normalizedThreshold) && normalizedThreshold >= 0
        ? normalizedThreshold
        : 1;

      if (!String(newItem.nom || '').trim()) {
        toast.error('Le nom de l article est obligatoire.');
        return;
      }

      if (!String(newItem.categorie || '').trim()) {
        toast.error('La categorie est obligatoire.');
        return;
      }

      const itemData = {
        nom: newItem.nom,
        categorie: newItem.categorie,
        unite: newItem.unite || 'kg',
        secteur: 'PARTICULIER',
        seuil_alerte: thresholdValue,
        perissable: !!newItem.perissable,
        dluo_dlc: newItem.dluo_dlc || null
      };
      
      const response = await axios.post('/items', itemData);
      const createdItem = response.data;
      const createdItemId = createdItem.id || createdItem.item_id;
      
      toast.success(addItemContext === 'suggestion' ? 'Article ajoute a la liste a acheter' : 'Article ajouté avec succès');

      let createdMovementId = null;
      if (addItemContext !== 'suggestion' && Number.isFinite(initialQuantity) && initialQuantity > 0 && createdItemId) {
        const movementResponse = await axios.post('/movements', {
          item_id: createdItemId,
          type: initialType,
          quantite: initialQuantity,
          prix: initialType === 'ACHAT' && Number.isFinite(initialPrice) && initialPrice > 0 ? initialPrice : null,
          note: 'Mouvement initial depuis nouvel article'
        });
        createdMovementId = movementResponse?.data?.id || null;
      }

      if (addItemContext === 'stock-manual' && createdItemId) {
        setPrixMagasinDefaultPrice(1);
        setPrixMagasinProductInfo({
          nom: createdItem.nom || itemData.nom,
          item_id: createdItemId,
        });
        setPendingPurchaseMovementId(createdMovementId || null);
        setShowPrixMagasinModal(true);
      }

      await loadData();
      resetNewItemForm();
      setShowAddItem(false);

      // Traiter le prochain article de la file suggestions
      if (suggestionQueue.length > 0) {
        const [nextSuggestion, ...remaining] = suggestionQueue;
        setSuggestionQueue(remaining);
        setTimeout(() => {
          applyQuickAddSuggestion(nextSuggestion);
          setAddItemContext('suggestion');
          setShowAddItem(true);
        }, 150);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'article:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout de l\'article');
    }
  };

  const handleBatchAddArticles = async () => {
    if (!Array.isArray(batchArticlesConfig) || batchArticlesConfig.length === 0) {
      toast.error('Aucun article à ajouter');
      return;
    }

    try {
      setLoading(true);
      let addedCount = 0;

      for (const config of batchArticlesConfig) {
        const initialQuantity = parseNumericValue(config.initial_quantite);
        const initialPrice = parseNumericValue(config.initial_prix);

        if (!Number.isFinite(initialQuantity) || initialQuantity <= 0) {
          toast.error(`Quantité invalide pour ${config.nom}`);
          continue;
        }
        try {
          // 1. Create article
          const itemData = {
            nom: config.nom,
            categorie: config.categorie,
            unite: config.unite,
            secteur: 'PARTICULIER',
            seuil_alerte: parseFloat(config.seuil_alerte),
            perissable: false,
            dluo_dlc: null
          };
          
          const itemResponse = await axios.post('/items', itemData);
          const createdItemId = itemResponse.data.id || itemResponse.data.item_id;

          // 2. Create purchase movement
          let createdMovementId = null;
          if (createdItemId) {
            const movementResponse = await axios.post('/movements', {
              item_id: createdItemId,
              type: 'ACHAT',
              quantite: initialQuantity,
              prix: Number.isFinite(initialPrice) && initialPrice > 0 ? initialPrice : null,
              note: 'Mouvement initial depuis sélection suggestions'
            });
            createdMovementId = movementResponse?.data?.id || null;
          }

          addedCount++;
        } catch (innerError) {
          console.error(`Erreur pour ${config.nom}:`, innerError);
        }
      }

      if (addedCount > 0) {
        toast.success(`${addedCount} article${addedCount > 1 ? 's' : ''} ajouté${addedCount > 1 ? 's' : ''} avec succès`);
        await loadData();
        setBatchArticlesConfig([]);
        setShowBatchArticlesModal(false);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout batch:', error);
      toast.error('Erreur lors de l\'ajout des articles');
    } finally {
      setLoading(false);
    }
  };

  const closeOnboarding = () => {
    if (user?.id) {
      localStorage.setItem(`smartinventory_onboarding_seen_${user.id}`, 'true');
    }
    setShowOnboarding(false);
  };

  const handleAddMovement = async (e) => {
    e.preventDefault();
    const itemId = newMovement.item_id;
    const quantity = parseNumericValue(newMovement.quantite);

    if (!itemId) {
      toast.error('Veuillez sélectionner un article');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Veuillez entrer une quantité valide');
      return;
    }

    try {
      const movementData = {
        ...newMovement,
        quantite: quantity,
        source: newMovement.source?.trim() || undefined,
        note: newMovement.note?.trim() || undefined
      };
      
      await axios.post('/movements', movementData);
      toast.success('Mouvement enregistré avec succès');
      
      if (newMovement.type === 'ACHAT') {
        const item = items.find(i => i.id === itemId);
        if (item) {
          setEstimationProductInfo({
            nom: item.nom,
            marque: item.marque || '',
            categorie: item.categorie,
            quantite_detectee: item.stock || quantity,
            unite_detectee: item.unite,
            item_id: item.id
          });
          setShowEstimationModal(true);
        }
      }
      
      setNewMovement({
        item_id: '',
        type: 'ACHAT',
        quantite: '',
        source: '',
        note: ''
      });
      setShowMovement(false);
      await loadData();
    } catch (error) {
      console.error('handleAddMovement error:', error.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du mouvement');
    }
  };

  const quickMovement = async (itemId, type, quantity = 1) => {
    console.log('quickMovement called:', itemId, type, quantity);
    
    try {
      const response = await axios.post('/movements', {
        item_id: itemId,
        type: type === 'ACHAT' ? 'ACHAT' : 'CONSOMMATION',
        quantite: quantity,
        note: type === 'ACHAT' ? 'Achat rapide' : 'Consommation intelligente'
      });
      
      console.log('Movement response:', response.data);
      
      // Ne pas afficher de toast ici si appelé depuis confirmPurchase
      if (type !== 'ACHAT' || !isProcessing) {
        toast.success(
          type === 'ACHAT' 
            ? `+${quantity} ajouté au stock` 
            : `${quantity} consommé (apprentissage en cours)`
        );
      }
      
      await loadData(); // Attendre que les données soient rechargées
      return response.data;
    } catch (error) {
      console.error('quickMovement error:', error);
      toast.error('Erreur lors de l\'enregistrement du mouvement');
      throw error; // Re-throw pour que confirmPurchase puisse gérer l'erreur
    }
  };

  const handleQuickPurchase = (itemId) => {
    setSelectedItemForPurchase(itemId);
    setPurchaseQuantity('');
    setShowQuantityPopup(true);
  };

  const handleEstimationValidate = async (estimationData) => {
    try {
      await axios.post('/items/configure-estimation', {
        item_id: estimationProductInfo.item_id,
        consommation_jour: estimationData.consommation_jour,
        unite: estimationData.unite
      });
      
      toast.success(`Estimation enregistrée: ${estimationData.duree_estimee_jours} jours`);
      
      setPrixMagasinProductInfo({
        nom: estimationProductInfo.nom,
        item_id: estimationProductInfo.item_id
      });
      setShowPrixMagasinModal(true);
    } catch (error) {
      console.error('Erreur sauvegarde estimation:', error.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde de l\'estimation');
      setPrixMagasinProductInfo({
        nom: estimationProductInfo.nom,
        item_id: estimationProductInfo.item_id
      });
      setShowPrixMagasinModal(true);
    }
  };

  const confirmPurchase = async () => {
    if (isProcessing) {
      console.log('Purchase already in progress, ignoring click');
      return;
    }

    const quantity = parseNumericValue(purchaseQuantity);
    if (!selectedItemForPurchase) {
      toast.error('Article non sélectionné');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Veuillez entrer une quantité valide');
      return;
    }

    setIsProcessing(true);
    console.log('Starting purchase process:', selectedItemForPurchase, quantity);
    
    try {
      const movementResponse = await quickMovement(selectedItemForPurchase, 'ACHAT', quantity);
      console.log('Purchase completed successfully');
      
      const item = items.find(i => i.id === selectedItemForPurchase);
      const stockItem = stockStatus.find((entry) => entry.item_id === selectedItemForPurchase);
      if (item) {
        const estimatedPriceCandidate =
          stockItem?.prix_resolved ??
          stockItem?.prix_moyen ??
          item?.prix_resolved ??
          item?.prix_moyen ??
          null;
        const estimatedStoreCandidate =
          stockItem?.magasin_moins_cher ??
          item?.magasin_moins_cher ??
          null;

        setPendingPurchaseMovementId(movementResponse?.id || null);
        setPurchaseTransition({
          itemId: item.id,
          itemName: item.nom,
          category: item.categorie,
          quantity,
          estimatedPrice: stockItem?.prix_reel != null ? Number(stockItem.prix_reel) * quantity : null,
        });
        setPrixMagasinProductInfo({
          nom: item.nom,
          item_id: item.id,
          prix_moyen: estimatedPriceCandidate,
          prix_resolved: estimatedPriceCandidate,
          magasin_moins_cher: estimatedStoreCandidate,
          prix_source: estimatedPriceCandidate != null ? 'estimated' : 'unknown'
        });
        setShowPrixMagasinModal(true);
      }
      
      setShowQuantityPopup(false);
      setSelectedItemForPurchase(null);
      setPurchaseQuantity('');
      toast.success('Achat enregistré avec succès');
    } catch (error) {
      console.error('Purchase error:', error.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({...item});
    setShowEditItem(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/items/${editingItem.id}`, {
        nom: editingItem.nom,
        categorie: editingItem.categorie,
        unite: editingItem.unite,
        secteur: editingItem.secteur,
        seuil_alerte: parseFloat(editingItem.seuil_alerte),
        perissable: editingItem.perissable,
        dluo_dlc: editingItem.dluo_dlc || null
      });
      
      toast.success('Article mis à jour avec succès');
      setShowEditItem(false);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEditProfile = () => {
    setEditProfileData({ 
      nb_personnes: user?.nb_personnes || 1,
      report_days: user?.report_days || ['mercredi', 'vendredi'],
      report_time: user?.report_time || '12:00'
    });
    setShowEditProfile(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      if (!editProfileData.report_days || editProfileData.report_days.length === 0) {
        toast.error('Veuillez sélectionner au moins un jour de rapport');
        return;
      }

      // Mettre à jour le nombre de personnes
      await axios.put('/profile', null, {
        params: { nb_personnes: parseInt(editProfileData.nb_personnes) }
      });

      // Mettre à jour le planning des rapports (jours + heure)
      await axios.put('/profile/report-schedule', {
        report_days: editProfileData.report_days,
        report_time: editProfileData.report_time
      });

      toast.success('Profil mis à jour avec succès');
      setShowEditProfile(false);

      // Refresh user context without full page reload
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      loadData();
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      toast.error('Erreur lors de la mise à jour du profil');
    }
  };

  const handleDayToggle = (day) => {
    const currentDays = editProfileData.report_days || [];
    if (currentDays.includes(day)) {
      // Retirer le jour
      setEditProfileData({
        ...editProfileData,
        report_days: currentDays.filter(d => d !== day)
      });
    } else {
      // Ajouter le jour
      setEditProfileData({
        ...editProfileData,
        report_days: [...currentDays, day]
      });
    }
  };

  const handleAllDaysToggle = () => {
    const allDays = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
    const currentDays = editProfileData.report_days || [];
    
    if (currentDays.length === 7) {
      // Désélectionner tous
      setEditProfileData({...editProfileData, report_days: []});
    } else {
      // Sélectionner tous
      setEditProfileData({...editProfileData, report_days: allDays});
    }
  };

  const getAiEstimation = async (itemId) => {
    try {
      const response = await axios.get(`/ai/consumption-estimate/${itemId}`);
      console.log('AI Estimation Response:', response.data);
      
      if (response.data) {
        setAiEstimations(prev => ({
          ...prev,
          [itemId]: response.data
        }));
        toast.success('Estimation IA générée avec succès');
        // Recharger les patterns d'apprentissage
        await loadData();
      } else {
        throw new Error('Réponse IA vide');
      }
    } catch (error) {
      console.error('Erreur estimation IA:', error);
      toast.error(`Erreur estimation IA: ${error.response?.data?.detail || error.message}`);
    }
  };

  const addSuggestedItem = async (suggestedItem) => {
    try {
      const itemData = {
        ...suggestedItem,
        secteur: 'PARTICULIER',
        seuil_alerte: suggestedItem.seuil,
        perissable: ['lait', 'pain'].includes(suggestedItem.categorie)
      };
      
      await axios.post('/items', itemData);
      toast.success(`${suggestedItem.nom} ajouté à votre liste`);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de l\'article');
    }
  };

  const deleteItem = (itemId, itemName) => {
    console.log('deleteItem called with:', itemId, itemName);
    setDeleteConfirmation({ show: true, itemId, itemName });
  };

  const confirmDelete = async () => {
    const { itemId, itemName } = deleteConfirmation;
    console.log('Confirming deletion for:', itemId, itemName);
    
    try {
      setDeleteConfirmation({ show: false, itemId: null, itemName: '' });
      console.log('Attempting to delete item:', itemId);
      const response = await axios.delete(`/items/${itemId}`);
      console.log('Delete response:', response);
      
      toast.success(`"${itemName}" supprimé avec succès`);
      await loadData(); // Recharger toutes les données
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(`Erreur lors de la suppression: ${error.response?.data?.detail || error.message}`);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, itemId: null, itemName: '' });
  };

  const sendDailyReport = async () => {
    try {
      const response = await axios.post('/reports/send-daily');
      if (response.data.error) {
        toast.error(response.data.message);
      } else {
        toast.success(`Rapport envoyé à ${response.data.details?.email || 'votre email'} (${response.data.details?.total_articles || 0} articles)`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du rapport');
    }
  };

  // Fonction testEmail supprimée - plus besoin de test manuel

  const handleClearToBuyList = async () => {
    try {
      const clearResponse = await axios.post('/items/shopping-list/clear');
      const clearedIds = Array.isArray(clearResponse.data?.cleared_item_ids)
        ? clearResponse.data.cleared_item_ids
        : [];
      if (clearedIds.length > 0) {
        setHiddenShoppingItemIds((prev) => [...new Set([...prev, ...clearedIds])]);
      }
      await axios.post('/items/archive-stale').catch(() => null);
      toast.success('Liste a acheter videe');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du vidage de la liste a acheter');
    }
  };

  const archiveAllStockItems = async () => {
    try {
      await axios.post('/items/archive-stock');
      toast.success('Stock archive avec succes');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l archivage du stock');
    }
  };

  const handleArchiveItem = async (itemId) => {
    try {
      await axios.post(`/items/${itemId}/archive`);
      toast.success('Article archive');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l archivage de l article');
    }
  };

  // Fonction pour vérifier si un article est connu
  const checkArticleKnown = async (nom, unite, nb_personnes) => {
    if (!nom || nom.length < 3) return; // Ne vérifier qu'avec au moins 3 caractères
    
    try {
      const response = await axios.post('/items/check-known', {
        nom: nom,
        unite: unite,
        nb_personnes: nb_personnes || user?.nb_personnes || 1
      });
      
      setArticleKnown(response.data);
      setShowConsumptionInput(!response.data.known);
      
      if (response.data.known && response.data.estimation) {
        console.log('Article connu:', response.data.suggestion_text);
        toast.success(`Article reconnu ! ${response.data.suggestion_text}`);
      }
    } catch (error) {
      console.error('Erreur vérification article:', error);
      setArticleKnown(null);
      setShowConsumptionInput(true); // Par défaut, demander estimation
    }
  };

  // Email configuration functions removed - now handled via environment variables

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (changePasswordData.new_password !== changePasswordData.confirm_password) {
      toast.error('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (changePasswordData.new_password.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await axios.post('/auth/change-password', {
        current_password: changePasswordData.current_password,
        new_password: changePasswordData.new_password
      });
      
      toast.success('Mot de passe modifié avec succès');
      setShowChangePassword(false);
      setChangePasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountPassword) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    try {
      setIsDeletingAccount(true);
      await axios.delete('/user/account', {
        data: { password: deleteAccountPassword }
      });
      toast.success('Compte supprime avec succes');
      setShowDeleteAccountConfirm(false);
      setShowChangePassword(false);
      setDeleteAccountPassword('');
      logout('Votre compte a ete supprime definitivement.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Suppression du compte impossible');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const toggleFilterValue = (setter, value) => {
    setter((previous) => (
      previous.includes(value)
        ? previous.filter((entry) => entry !== value)
        : [...previous, value]
    ));
  };

  const movementStatsByItemId = useMemo(() => {
    const stats = new Map();
    movements.forEach((movement) => {
      if (!movement?.item_id) return;
      const entry = stats.get(movement.item_id) || {
        purchaseCount: 0,
        totalPurchasedQuantity: 0,
        lastPurchaseAt: 0,
        lastActivityAt: 0,
      };
      const movementType = String(movement.type || '').toUpperCase();
      const movementTimestamp = new Date(movement.date || movement.created_at || 0).getTime() || 0;
      if (movementTimestamp) {
        entry.lastActivityAt = Math.max(entry.lastActivityAt, movementTimestamp);
      }
      if (movementType === 'ACHAT') {
        entry.purchaseCount += 1;
        entry.totalPurchasedQuantity += Math.abs(parseNumericValue(movement.quantite) || 0);
        if (movementTimestamp) {
          entry.lastPurchaseAt = Math.max(entry.lastPurchaseAt, movementTimestamp);
        }
      }
      stats.set(movement.item_id, entry);
    });
    return stats;
  }, [movements]);

  const smartStockEntries = useMemo(() => {
    const itemById = new Map(items.map((item) => [item.id, item]));
    const trackedStockItemIds = new Set();
    const grouped = new Map();

    stockStatus.forEach((stock) => {
      trackedStockItemIds.add(stock.item_id);
      const linkedItem = itemById.get(stock.item_id);
      const category = String(stock.categorie || linkedItem?.categorie || 'autres').toLowerCase();
      const unit = stock.unite || linkedItem?.unite || '';
      const rawQuantity = stock.stock_actuel;
      const hasQuantity = rawQuantity !== null && rawQuantity !== undefined && rawQuantity !== '';
      const quantity = Number(rawQuantity || 0);
      const threshold = Number(stock.seuil_alerte || linkedItem?.seuil_alerte || 1);
      const movementStats = movementStatsByItemId.get(stock.item_id) || {
        purchaseCount: 0,
        totalPurchasedQuantity: 0,
        lastPurchaseAt: 0,
        lastActivityAt: 0,
      };
      const fallbackDailyConsumption = Number(stock.total_household_daily || linkedItem?.consommation_jour || 0);
      const daysSincePurchase = getDaysSince(movementStats.lastPurchaseAt);
      const existingEstimatedDays = Number(stock.jours_restants);
      const estimatedDaysRemaining = Number.isFinite(existingEstimatedDays) && existingEstimatedDays > 0
        ? existingEstimatedDays
        : (fallbackDailyConsumption > 0 ? quantity / fallbackDailyConsumption : Number.POSITIVE_INFINITY);
      const stockType = inferStockType({
        category,
        purchaseCount: movementStats.purchaseCount,
        daysSincePurchase,
      });
      const stockLevel = inferStockStatus({
        isAlert: Boolean(stock.en_alerte),
        hasQuantity,
        quantity,
        threshold,
        estimatedDaysRemaining,
      });
      const isArchived = stockType === 'one_shot' || (Number.isFinite(daysSincePurchase) && daysSincePurchase >= 14);
      const section = isArchived
        ? 'archive'
        : (stockLevel === 'faible' ? 'watch' : (stockLevel === 'moyen' ? 'inUse' : 'ok'));

      const groupingKey = `${normalizeStockName(stock.item_nom)}::${category}::${unit}`;
      const existingGroup = grouped.get(groupingKey);
      if (!existingGroup) {
        grouped.set(groupingKey, {
          key: groupingKey,
          itemName: stock.item_nom,
          category,
          unit,
          quantity,
          threshold,
          isAlert: Boolean(stock.en_alerte),
          estimatedDaysRemaining,
          stockType,
          stockLevel,
          section,
          purchaseCount: movementStats.purchaseCount,
          totalPurchasedQuantity: movementStats.totalPurchasedQuantity,
          lastPurchaseAt: movementStats.lastPurchaseAt,
          lastActivityAt: movementStats.lastActivityAt,
          displayEmoji: getSuggestionEmoji(category),
          alternativePrice: alternativePriceSuggestions[stock.item_id] || null,
          entries: [stock],
          primaryEntry: stock,
          linkedItem,
          typeLabels: new Set([STOCK_TYPE_LABELS[stockType] || stockType]),
        });
        return;
      }

      existingGroup.quantity += quantity;
      existingGroup.threshold = Math.max(existingGroup.threshold, threshold);
      existingGroup.isAlert = existingGroup.isAlert || Boolean(stock.en_alerte);
      existingGroup.estimatedDaysRemaining = Math.min(existingGroup.estimatedDaysRemaining, estimatedDaysRemaining);
      existingGroup.purchaseCount += movementStats.purchaseCount;
      existingGroup.totalPurchasedQuantity += movementStats.totalPurchasedQuantity;
      existingGroup.lastPurchaseAt = Math.max(existingGroup.lastPurchaseAt, movementStats.lastPurchaseAt);
      existingGroup.lastActivityAt = Math.max(existingGroup.lastActivityAt, movementStats.lastActivityAt);
      existingGroup.entries.push(stock);
      existingGroup.typeLabels.add(STOCK_TYPE_LABELS[stockType] || stockType);

      const sectionPriority = { watch: 0, inUse: 1, ok: 2, archive: 3 };
      const existingPriority = sectionPriority[existingGroup.section] ?? 3;
      const incomingPriority = sectionPriority[section] ?? 3;
      if (incomingPriority < existingPriority) {
        existingGroup.section = section;
        existingGroup.stockType = stockType;
        existingGroup.stockLevel = stockLevel;
      }
      if ((stock.prix_reel || 0) > (existingGroup.primaryEntry?.prix_reel || 0)) {
        existingGroup.primaryEntry = stock;
        existingGroup.alternativePrice = alternativePriceSuggestions[stock.item_id] || existingGroup.alternativePrice;
      }
    });

    items
      .filter((item) => item?.id && !item.is_archived && !trackedStockItemIds.has(item.id))
      .forEach((item) => {
        const category = String(item.categorie || 'autres').toLowerCase();
        const unit = item.unite || '';
        const threshold = Number(item.seuil_alerte || 1);
        const stockFallback = {
          item_id: item.id,
          item_nom: item.nom,
          categorie: category,
          unite: unit,
          stock_actuel: 0,
          seuil_alerte: threshold,
          user_id: item.user_id,
          en_alerte: false,
          prix_reel: null,
          magasin_achat: null,
          total_household_daily: item.consommation_jour ?? null,
          per_person_daily: null,
        };
        const stockType = inferStockType({
          category,
          purchaseCount: 0,
          daysSincePurchase: Number.POSITIVE_INFINITY,
        });
        const groupingKey = `${normalizeStockName(item.nom)}::${category}::${unit}`;
        if (grouped.has(groupingKey)) {
          return;
        }
        grouped.set(groupingKey, {
          key: groupingKey,
          itemName: item.nom,
          category,
          unit,
          quantity: 0,
          threshold,
          isAlert: false,
          estimatedDaysRemaining: Number.POSITIVE_INFINITY,
          stockType,
          stockLevel: 'moyen',
          section: 'inUse',
          purchaseCount: 0,
          totalPurchasedQuantity: 0,
          lastPurchaseAt: 0,
          lastActivityAt: 0,
          displayEmoji: getSuggestionEmoji(category),
          alternativePrice: null,
          entries: [stockFallback],
          primaryEntry: stockFallback,
          linkedItem: item,
          typeLabels: new Set([STOCK_TYPE_LABELS[stockType] || stockType]),
        });
      });

    return Array.from(grouped.values()).sort((left, right) => {
      const sectionOrder = { watch: 0, inUse: 1, ok: 2, archive: 3 };
      const sectionDelta = (sectionOrder[left.section] ?? 4) - (sectionOrder[right.section] ?? 4);
      if (sectionDelta !== 0) return sectionDelta;
      if (left.stockLevel !== right.stockLevel) {
        const levelOrder = { faible: 0, moyen: 1, ok: 2 };
        return (levelOrder[left.stockLevel] ?? 3) - (levelOrder[right.stockLevel] ?? 3);
      }
      if (left.estimatedDaysRemaining !== right.estimatedDaysRemaining) {
        return left.estimatedDaysRemaining - right.estimatedDaysRemaining;
      }
      return left.itemName.localeCompare(right.itemName, 'fr', { sensitivity: 'base' });
    });
  }, [stockStatus, items, movementStatsByItemId, alternativePriceSuggestions]);

  const availableStockCategories = useMemo(() => {
    const set = new Set(smartStockEntries.map((entry) => entry.category));
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [smartStockEntries]);

  const filteredSmartStockEntries = useMemo(() => {
    return smartStockEntries.filter((entry) => {
      if (stockCategoryFilters.length > 0 && !stockCategoryFilters.includes(entry.category)) {
        return false;
      }
      if (stockStatusFilters.length > 0 && !stockStatusFilters.includes(entry.stockLevel)) {
        return false;
      }
      if (stockTypeFilters.length > 0 && !stockTypeFilters.includes(entry.stockType)) {
        return false;
      }
      return true;
    });
  }, [smartStockEntries, stockCategoryFilters, stockStatusFilters, stockTypeFilters]);

  const stockSections = useMemo(() => ({
    watch: filteredSmartStockEntries.filter((entry) => entry.section === 'watch'),
    inUse: filteredSmartStockEntries.filter((entry) => entry.section === 'inUse'),
    ok: filteredSmartStockEntries.filter((entry) => entry.section === 'ok'),
    archive: filteredSmartStockEntries.filter((entry) => entry.section === 'archive'),
  }), [filteredSmartStockEntries]);

  const proactiveShortageMessages = useMemo(() => {
    return stockSections.watch
      .filter((entry) => Number.isFinite(entry.estimatedDaysRemaining) && entry.estimatedDaysRemaining >= 0)
      .slice(0, 3)
      .map((entry) => `Tu vas manquer de ${entry.itemName.toLowerCase()} dans ${Math.max(1, Math.round(entry.estimatedDaysRemaining))} jour(s)`);
  }, [stockSections.watch]);

  const currentTheme = DASHBOARD_THEMES[dashboardTheme] || DASHBOARD_THEMES.emerald;
  const activeTabConfig = DASHBOARD_TABS.find((tab) => tab.value === activeTab) || DASHBOARD_TABS[0];
  const ActiveTabIcon = activeTabConfig.icon;
  const isOverview = activeTab === 'overview';
  const stockByItemId = new Map(stockStatus.map((stock) => [stock.item_id, stock]));
  const watchStockItemIds = useMemo(() => {
    const ids = new Set();
    smartStockEntries
      .filter((entry) => entry.section === 'watch')
      .forEach((entry) => {
        entry.entries.forEach((stockEntry) => {
          if (stockEntry?.item_id) {
            ids.add(stockEntry.item_id);
          }
        });
      });
    return ids;
  }, [smartStockEntries]);
  const toBuyStockItems = stockStatus.filter((item) => watchStockItemIds.has(item.item_id));
  const toBuyMissingStockItems = items
    .filter((item) => !item.is_archived && !stockByItemId.has(item.id))
    .map((item) => ({
      item_id: item.id,
      item_nom: item.nom,
      categorie: item.categorie,
      unite: item.unite,
      seuil_alerte: item.seuil_alerte ?? 1,
      stock_actuel: 0,
      en_alerte: true,
      prix_reel: null,
      user_id: item.user_id,
    }));
  const toBuyItems = [...toBuyStockItems, ...toBuyMissingStockItems]
    .filter((item) => !hiddenShoppingItemIds.includes(item.item_id))
    .sort((left, right) => {
      const leftPrice = Number(left.prix_reel || 0);
      const rightPrice = Number(right.prix_reel || 0);
      return rightPrice - leftPrice;
    });
  const alertsCount = toBuyItems.length;
  const categoryBudgetHighlights = (budgetSummary?.categories || []).slice(0, 3);
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthlySpent = movements.reduce((total, movement) => {
    const movementDate = new Date(movement.date || movement.created_at || 0);
    const isPurchase = String(movement.type || '').toUpperCase() === 'ACHAT';
    const movementPrice = Number(movement.prix || 0);
    if (!isPurchase || !movementPrice || movementDate.getMonth() !== currentMonth || movementDate.getFullYear() !== currentYear) {
      return total;
    }
    return total + movementPrice;
  }, 0);
  const selectedPurchaseStockItem = selectedItemForPurchase
    ? stockStatus.find((stock) => stock.item_id === selectedItemForPurchase)
    : null;
  const selectedPurchaseBaseItem = selectedItemForPurchase
    ? items.find((item) => item.id === selectedItemForPurchase)
    : null;
  const selectedPurchaseDisplayName = selectedPurchaseStockItem?.item_nom || selectedPurchaseBaseItem?.nom || '';
  const selectedPurchaseDisplayUnit = selectedPurchaseStockItem?.unite || selectedPurchaseBaseItem?.unite || '';
  const tabMetrics = {
    achats: alertsCount,
    stock: stockStatus.length,
    budget: budgetSummary ? `${Math.round(budgetSummary.global_remaining || 0)}€` : null,
    historique: movements.length,
    foyer: user?.nb_personnes || 1,
  };

  const renderSmartStockCard = (smartStock) => {
    const stock = smartStock.primaryEntry;
    const linkedItem = smartStock.linkedItem;
    const currentStock = smartStock.quantity;
    const isEditing = editingQuantityId === stock.item_id;
    const isOwned = stock.user_id === user?.id;
    const cheaperAlternative = smartStock.alternativePrice;
    const displayType = STOCK_TYPE_LABELS[smartStock.stockType] || smartStock.stockType;
    const displayStatus = STOCK_STATUS_LABELS[smartStock.stockLevel] || smartStock.stockLevel;
    const daysRemainingText = Number.isFinite(smartStock.estimatedDaysRemaining)
      ? `${smartStock.estimatedDaysRemaining.toFixed(1)} ${t('stock.daysShort')}`
      : 'N/A';

    const saveEditedStockAndPrice = async () => {
      const newQty = parseFloat(tempQuantity);
      if (!Number.isFinite(newQty) || newQty < 0) {
        toast.error('Quantite invalide');
        return;
      }

      const hasPriceInput = String(tempPrice || '').trim() !== '';
      const parsedPrice = parseNumericValue(tempPrice);
      if (hasPriceInput && (!Number.isFinite(parsedPrice) || parsedPrice < 0)) {
        toast.error('Prix invalide');
        return;
      }

      const payload = { stock: newQty };
      if (hasPriceInput) {
        payload.prix_reel = parsedPrice;
      }

      try {
        await axios.put(`/items/${stock.item_id}`, payload);
        toast.success('Article mis a jour');
        loadData();
        setEditingQuantityId(null);
      } catch (error) {
        toast.error('Erreur lors de la mise a jour');
      }
    };

    return (
      <Card
        key={smartStock.key}
        className={`overflow-hidden border shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl ${smartStock.section === 'watch' ? 'border-red-200 bg-red-50/80' : 'border-emerald-100 bg-white'}`}
        data-testid={`stock-item-${stock.item_id}`}
      >
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-4">
                <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-4xl ${smartStock.section === 'watch' ? 'bg-red-100' : 'bg-emerald-100'}`}>
                  <span aria-hidden="true">{smartStock.displayEmoji}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-lg font-semibold text-gray-900 sm:text-xl">
                      {smartStock.itemName}
                      {smartStock.purchaseCount > 1 ? ` (x${smartStock.purchaseCount})` : ''}
                    </h3>
                    {smartStock.section === 'watch' && smartStock.stockType === 'consommable' && (
                      <Badge variant="destructive" className="text-xs">{t('stock.alert')}</Badge>
                    )}
                    <Badge className="bg-slate-100 text-slate-800">{displayType}</Badge>
                    <Badge className="bg-amber-100 text-amber-800">{displayStatus}</Badge>
                    {stock.magasin_achat && (
                      <Badge className="bg-blue-100 text-blue-800">{stock.magasin_achat}</Badge>
                    )}
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-2xl bg-emerald-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">{t('stock.stockLabel')}</p>
                      <p className="mt-1 text-base font-bold text-emerald-900">{currentStock} {smartStock.unit}</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t('stock.threshold')}</p>
                      <p className="mt-1 text-base font-semibold text-gray-900">{smartStock.threshold} {smartStock.unit}</p>
                    </div>
                    <div className="rounded-2xl bg-amber-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-600">{t('stock.remaining')}</p>
                      <p className="mt-1 text-base font-semibold text-amber-900">{daysRemainingText}</p>
                    </div>
                    {stock.prix_reel != null && (
                      <div className="rounded-2xl bg-cyan-50 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-cyan-700">Prix</p>
                        <p className="mt-1 text-base font-semibold text-cyan-900" data-testid={`stock-price-${stock.item_id}`}>{stock.prix_reel.toFixed(2)} €</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600" data-testid={`stock-estimation-${stock.item_id}`}>
                    {stock.per_person_daily != null && (
                      <Badge variant="outline" className="border-emerald-200 bg-white text-emerald-700">
                        {stock.per_person_daily} {smartStock.unit}/{t('stock.perPersonDay')}
                      </Badge>
                    )}
                    {(stock.total_household_daily != null || linkedItem?.consommation_jour != null) && (
                      <Badge className="bg-emerald-100 text-emerald-800">
                        {(Number(stock.total_household_daily || linkedItem?.consommation_jour || 0)).toFixed(2)} {smartStock.unit}/{t('stock.totalHouseholdDay')}
                      </Badge>
                    )}
                    {smartStock.lastPurchaseAt > 0 && (
                      <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                        Dernier achat: {getDaysSince(smartStock.lastPurchaseAt)} j
                      </Badge>
                    )}
                  </div>

                  {cheaperAlternative && (
                    <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                      <p className="text-sm font-medium text-amber-900">Alternative plus economique observee</p>
                      <p className="mt-1 text-sm text-amber-800">{cheaperAlternative.message}</p>
                      <p className="mt-1 text-xs text-amber-700">
                        {cheaperAlternative.alternative_store} • {cheaperAlternative.alternative_price.toFixed(2)} € • economie potentielle {cheaperAlternative.savings.toFixed(2)} €
                      </p>
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFoodEducationSheet({
                          nom: smartStock.itemName,
                          categorie: smartStock.category,
                          ...linkedItem,
                          ...stock,
                        });
                      }}
                      className="h-8 rounded-full border-emerald-200 px-3 text-xs text-emerald-700 hover:bg-emerald-50"
                    >
                      Fiche nutritive
                    </Button>
                    {!isEditing ? (
                      <>
                        {isOwned ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingQuantityId(stock.item_id);
                              setTempQuantity(currentStock.toString());
                              setTempPrice(stock.prix_reel != null ? String(stock.prix_reel) : '');
                            }}
                            className="h-8 rounded-full border-blue-200 px-3 text-xs text-blue-700 hover:bg-blue-50"
                            title={t('articles.edit')}
                          >
                            <Edit3 size={12} className="mr-1" /> {t('articles.edit')}
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-500">Seulement modifiable par le proprietaire</span>
                        )}
                      </>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-gray-50 p-2">
                        <Input
                          type="number"
                          value={tempQuantity}
                          onChange={(e) => setTempQuantity(e.target.value)}
                          className="h-9 w-24 text-sm"
                          autoFocus
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              await saveEditedStockAndPrice();
                            }
                            if (e.key === 'Escape') {
                              setEditingQuantityId(null);
                            }
                          }}
                        />
                        <span className="text-sm text-gray-600">{smartStock.unit}</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={tempPrice}
                          onChange={(e) => setTempPrice(e.target.value)}
                          className="h-9 w-24 text-sm"
                          placeholder="Prix €"
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              await saveEditedStockAndPrice();
                            }
                            if (e.key === 'Escape') {
                              setEditingQuantityId(null);
                            }
                          }}
                        />
                        <span className="text-sm text-gray-600">€</span>
                        <Button
                          size="sm"
                          onClick={saveEditedStockAndPrice}
                          className="h-8 rounded-full bg-green-600 px-3 text-xs text-white hover:bg-green-700"
                        >
                          ✓
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingQuantityId(null)}
                          className="h-8 rounded-full px-3 text-xs"
                        >
                          ✕
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end lg:w-[220px] lg:grid-cols-1">
              <Button
                size="sm"
                onClick={() => getAiEstimation(stock.item_id)}
                className="h-10 rounded-2xl bg-purple-600 px-3 text-white hover:bg-purple-700 sm:h-11"
                data-testid={`ai-estimate-${stock.item_id}`}
                title={t('ai.estimate')}
              >
                <Brain size={14} className="mr-2" />
                <span className="text-xs sm:text-sm">IA</span>
              </Button>
              <Button
                size="sm"
                onClick={() => !isProcessing && handleQuickPurchase(stock.item_id)}
                disabled={isProcessing}
                className="h-10 rounded-2xl bg-green-600 px-3 text-white hover:bg-green-700 disabled:bg-gray-400 sm:h-11"
                data-testid={`quick-buy-${stock.item_id}`}
                title={t('stock.buy')}
              >
                <ShoppingCart size={14} className="mr-2" />
                <span className="text-xs sm:text-sm">{t('stock.buy')}</span>
              </Button>
              <Button
                size="sm"
                onClick={() => !isProcessing && quickMovement(stock.item_id, 'CONSO', 0.1)}
                disabled={isProcessing}
                className="h-10 rounded-2xl bg-orange-600 px-3 text-white hover:bg-orange-700 disabled:bg-gray-400 sm:h-11"
                data-testid={`quick-consume-${stock.item_id}`}
                title={t('stock.consume')}
              >
                <Minus size={14} className="mr-2" />
                <span className="text-xs sm:text-sm">{t('stock.consume')}</span>
              </Button>
              {isOwned && linkedItem && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedArticleForConfig(linkedItem);
                    setShowConfigModal(true);
                  }}
                  className="h-10 rounded-2xl border-orange-200 px-3 text-orange-700 hover:bg-orange-50 sm:h-11"
                  title={t('articles.edit')}
                >
                  <Settings size={14} className="mr-2" />
                  <span className="text-xs sm:text-sm">Config</span>
                </Button>
              )}
              {isOwned && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleArchiveItem(stock.item_id)}
                  className="h-10 rounded-2xl border-slate-300 px-3 text-slate-700 hover:bg-slate-50 sm:h-11"
                  data-testid={`archive-item-${stock.item_id}`}
                >
                  <Archive size={14} className="mr-2" />
                  <span className="text-xs sm:text-sm">Archiver</span>
                </Button>
              )}
              {isOwned && (
                <Button
                  size="sm"
                  onClick={() => deleteItem(stock.item_id, stock.item_nom)}
                  className="h-10 rounded-2xl bg-red-600 px-3 text-white hover:bg-red-700 sm:h-11"
                  data-testid={`delete-item-${stock.item_id}`}
                  title={t('articles.delete')}
                >
                  <Trash2 size={14} className="mr-2" />
                  <span className="text-xs sm:text-sm">{t('articles.delete')}</span>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          <p className="text-emerald-700 font-medium">{t('common.loadingFamily')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-clip bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100">
      {/* Grace Period One-Time Popup */}
      {gracePeriod.showPopup && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="grace-period-popup">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <AlertTriangle className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Abonnement annule</h3>
              <p className="text-sm text-gray-600 mt-2">
                Vos donnees seront conservees pendant <strong>{gracePeriod.daysLeft} jour(s)</strong>.
                {gracePeriod.deletionDate && (
                  <> Suppression prevue le <strong>{new Date(gracePeriod.deletionDate).toLocaleDateString('fr-FR')}</strong>.</>
                )}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Vous pouvez reactiver votre abonnement a tout moment pour conserver vos donnees.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={async () => {
                  setGracePeriod(prev => ({ ...prev, showPopup: false }));
                  try { await axios.post('/subscription/dismiss-popup'); } catch {}
                }}
                data-testid="grace-popup-dismiss"
              >
                J'ai compris
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={async () => {
                  setGracePeriod(prev => ({ ...prev, showPopup: false }));
                  try { await axios.post('/subscription/dismiss-popup'); } catch {}
                  setActiveTab('abonnement');
                }}
                data-testid="grace-popup-reactivate"
              >
                Reactiver
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Navigation */}
      <nav className="sticky top-0 z-40 overflow-x-clip border-b px-3 py-3 backdrop-blur-xl sm:px-6 sm:py-4" style={{ backgroundColor: currentTheme.panelBackground, borderColor: currentTheme.surfaceBorder }}>
        <div className="mx-auto flex max-w-7xl min-w-0 flex-wrap items-start justify-between gap-3 sm:items-center sm:gap-4">
          <div className="flex min-w-0 flex-1 items-center space-x-2 sm:flex-none sm:space-x-4">
            <div className="rounded-2xl p-2 shadow-lg" style={{ background: `linear-gradient(135deg, ${currentTheme.accent}, ${currentTheme.accentStrong})` }}>
              <Home className="w-5 h-5 text-white sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold sm:text-xl" style={{ color: currentTheme.surfaceText }}>{t('nav.family')}</h1>
              <p className="truncate text-xs sm:text-sm" style={{ color: currentTheme.mutedText }}>{user?.username} • {user?.nb_personnes || 1} {(user?.nb_personnes || 1) > 1 ? t('nav.persons') : t('nav.person')}</p>
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center justify-end gap-1.5 sm:w-auto sm:gap-3">
            {alertsCount > 0 && (
              <Badge variant="destructive" className="animate-pulse text-xs" data-testid="alerts-badge">
                {alertsCount} {alertsCount > 1 ? t('nav.alertsPlural') : t('nav.alerts')}
              </Badge>
            )}
            <NotificationSystem refreshToken={notificationRefreshToken} />
            <LanguageSwitcher variant="compact" />
            <Button
              variant="outline"
              onClick={handleEditProfile}
              className="flex h-8 items-center gap-1.5 border-0 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              style={{ backgroundColor: currentTheme.accentSoft, color: currentTheme.accentStrong }}
            >
              <Users size={14} />
              <span className="hidden sm:inline">Foyer</span>
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              className="flex h-8 items-center gap-1.5 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              data-testid="logout-button"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto max-w-7xl px-3 py-4 pb-24 sm:px-6 sm:py-8 sm:pb-10">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="grid min-w-0 gap-5 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start">
            <aside className={`${isOverview ? 'block' : 'hidden'} lg:sticky lg:top-24 lg:block`}>
              <div className="overflow-visible rounded-[28px] border p-4 shadow-2xl" style={{ background: currentTheme.sidebarBackground, borderColor: currentTheme.surfaceBorder }}>
                <div className="rounded-[24px] bg-white/10 p-4 text-white backdrop-blur">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/70">Dashboard</p>
                  <h2 className="mt-3 text-2xl font-semibold">{user?.username}</h2>
                  <p className="mt-2 text-sm text-white/70">{alertsCount} article(s) a surveiller • {stockStatus.length} en stock</p>
                </div>

                <TabsList className="mt-4 flex h-auto w-full flex-col items-stretch justify-start gap-3 rounded-3xl bg-transparent p-0 pr-1" data-testid="main-tabs">
                  {DASHBOARD_TABS.map((tab) => {
                    const Icon = tab.icon;
                    const metric = tabMetrics[tab.value];
                    return (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        data-testid={tab.testId || `${tab.value}-tab`}
                        className="group relative flex h-auto w-full items-start justify-start gap-3 whitespace-normal rounded-2xl border border-white/10 bg-white/10 px-4 py-4 text-left text-white shadow-none transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/16 data-[state=active]:border-white/20 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-[0_16px_40px_-22px_rgba(15,23,42,0.85)]"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/12 text-white transition-colors group-data-[state=active]:bg-slate-900 group-data-[state=active]:text-white">
                          <Icon size={18} strokeWidth={1.9} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block whitespace-normal break-words text-sm font-semibold">{tab.label || t(tab.labelKey)}</span>
                          <span className="mt-1 block break-words text-xs leading-5 text-white/65 group-data-[state=active]:text-slate-500">{tab.description}</span>
                        </span>
                        {metric ? (
                          <span className="ml-auto rounded-full bg-white/14 px-2.5 py-1 text-[11px] font-semibold group-data-[state=active]:bg-slate-100 group-data-[state=active]:text-slate-700">
                            {metric}
                          </span>
                        ) : null}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            </aside>

            <div className="min-w-0 space-y-4 sm:space-y-6">
              {isOverview ? (
                <>
                  <section className="overflow-hidden rounded-[30px] border p-5 shadow-2xl sm:p-7" style={{ background: currentTheme.heroBackground, borderColor: currentTheme.surfaceBorder }} data-testid="dashboard-overview">
                    <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                      <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em]" style={{ backgroundColor: currentTheme.accentSoft, color: currentTheme.accentStrong }}>
                          <Home className="h-4 w-4" />
                          Tableau de bord
                        </div>
                        <h2 className="mt-4 text-3xl font-semibold sm:text-4xl" style={{ color: currentTheme.surfaceText }}>Clique sur un menu a gauche pour ouvrir sa page a droite</h2>
                        <p className="mt-3 max-w-2xl text-sm leading-7 sm:text-base" style={{ color: currentTheme.mutedText }}>Le contenu n est plus empile en bas: chaque menu devient une vraie page detaillee avec une transition laterale et un retour rapide.</p>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3 xl:w-[420px]">
                        <div className="rounded-2xl p-4" style={{ backgroundColor: currentTheme.panelBackground, border: `1px solid ${currentTheme.surfaceBorder}` }}>
                          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: currentTheme.mutedText }}>A acheter</p>
                          <p className="mt-2 text-2xl font-bold" style={{ color: currentTheme.surfaceText }}>{alertsCount}</p>
                        </div>
                        <div className="rounded-2xl p-4" style={{ backgroundColor: currentTheme.panelBackground, border: `1px solid ${currentTheme.surfaceBorder}` }}>
                          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: currentTheme.mutedText }}>Depense du mois</p>
                          <p className="mt-2 text-2xl font-bold" style={{ color: currentTheme.surfaceText }}>{formatCurrencyValue(monthlySpent)}</p>
                        </div>
                        <div className="rounded-2xl p-4" style={{ backgroundColor: currentTheme.panelBackground, border: `1px solid ${currentTheme.surfaceBorder}` }}>
                          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: currentTheme.mutedText }}>Budget restant</p>
                          <p className={`mt-2 text-2xl font-bold ${getBudgetAlertTone(budgetSummary?.global_ratio || 0)}`}>{formatCurrencyValue(budgetSummary?.global_remaining || 0)}</p>
                        </div>
                      </div>
                    </div>

                    {purchaseTransition && (
                      <div className="mt-5 rounded-2xl border border-emerald-200 bg-white/85 p-4 shadow-sm transition-all duration-300 animate-in fade-in slide-in-from-bottom-2">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-emerald-700">{purchaseTransition.itemName} passe de la liste d achats au stock</p>
                            <p className="text-sm text-slate-600">+{purchaseTransition.quantity} ajoute au stock foyer • categorie {purchaseTransition.category}</p>
                          </div>
                          <div className="text-sm font-semibold text-emerald-700">
                            {purchaseTransition.estimatedPrice != null ? `impact budget estime ${formatCurrencyValue(purchaseTransition.estimatedPrice)}` : 'prix a confirmer dans le ticket'}
                          </div>
                        </div>
                      </div>
                    )}

                    {categoryBudgetHighlights.length > 0 && (
                      <div className="mt-6 grid gap-3 md:grid-cols-3">
                        {categoryBudgetHighlights.map((category) => (
                          <div key={category.category} className="rounded-2xl px-4 py-3" style={{ backgroundColor: currentTheme.panelBackground, border: `1px solid ${currentTheme.surfaceBorder}` }}>
                            <p className="text-xs uppercase tracking-[0.18em]" style={{ color: currentTheme.mutedText }}>{category.label}</p>
                            <div className="mt-2 flex items-end justify-between gap-4">
                              <div>
                                <p className="text-lg font-semibold" style={{ color: currentTheme.surfaceText }}>{formatCurrencyValue(category.spent)}</p>
                                <p className="text-xs" style={{ color: currentTheme.mutedText }}>reste {formatCurrencyValue(category.remaining)}</p>
                              </div>
                              <Badge className={`${category.ratio >= 1 ? 'bg-red-100 text-red-700' : category.ratio >= 0.85 ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {Math.round((category.ratio || 0) * 100)}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
                    <Card className="card hover-lift" data-testid="total-items-card">
                      <CardContent className="p-3 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">{t('stats.householdItems')}</p>
                            <p className="text-xl sm:text-2xl font-bold text-gray-900">{items.length}</p>
                          </div>
                          <Utensils className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="card hover-lift" data-testid="alerts-card">
                      <CardContent className="p-3 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">{t('stats.toBuy')}</p>
                            <p className="text-xl sm:text-2xl font-bold text-red-600">{alertsCount}</p>
                          </div>
                          <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="card hover-lift card-interactive cursor-pointer" onClick={handleEditProfile} data-testid="family-size-card" title={t('stats.clickToEdit')}>
                      <CardContent className="p-3 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600">{t('stats.householdSize')}</p>
                            <p className="text-xl sm:text-2xl font-bold text-blue-600">{user?.nb_personnes || 1}</p>
                            <p className="text-xs text-gray-500 mt-1"><Edit3 className="w-3 h-3 inline mr-1" />{t('stats.clickToEdit')}</p>
                          </div>
                          <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="card hover-lift">
                      <CardContent className="p-3 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-xs sm:text-sm font-medium text-gray-600">{t('stats.dailyReport')}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <Button size="sm" onClick={sendDailyReport} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1" data-testid="send-report-button">
                                <Mail size={12} className="mr-1" /> {t('stats.send')}
                              </Button>
                            </div>
                          </div>
                          <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                </>
              ) : (
                <div key={activeTab} className="overflow-hidden rounded-none shadow-2xl animate-in slide-in-from-right-10 fade-in-0 duration-300" style={{ background: currentTheme.heroBackground }}>
                  <div className="border-b px-5 py-4 sm:px-7" style={{ borderColor: currentTheme.surfaceBorder, backgroundColor: currentTheme.panelBackground }}>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        <Button type="button" variant="outline" onClick={() => setActiveTab('overview')} className="rounded-2xl border-0" style={{ backgroundColor: currentTheme.accentSoft, color: currentTheme.accentStrong }}>
                          Retour
                        </Button>
                        <div>
                          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em]" style={{ backgroundColor: currentTheme.accentSoft, color: currentTheme.accentStrong }}>
                            <ActiveTabIcon className="h-4 w-4" />
                            Menu ouvert
                          </div>
                          <h2 className="mt-3 text-2xl font-semibold sm:text-3xl" style={{ color: currentTheme.surfaceText }}>{activeTabConfig.label || t(activeTabConfig.labelKey)}</h2>
                          <p className="mt-2 max-w-2xl text-sm leading-6" style={{ color: currentTheme.mutedText }}>{activeTabConfig.description}</p>
                        </div>
                      </div>
                      <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: currentTheme.panelBackground, border: `1px solid ${currentTheme.surfaceBorder}` }}>
                        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: currentTheme.mutedText }}>Theme actif</p>
                        <p className="mt-1 text-sm font-semibold" style={{ color: currentTheme.surfaceText }}>{DASHBOARD_THEMES[dashboardTheme]?.label}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6">

          {/* Paywall Banner */}
          {paywallActive && activeTab !== 'abonnement' && (
            <div className="mt-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-5 text-center" data-testid="paywall-banner">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-1">{paywallMessage}</h3>
              <p className="text-sm text-gray-600 mb-4">Abonnez-vous pour acceder a toutes les fonctionnalites.</p>
              <Button
                onClick={() => setActiveTab('abonnement')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-11"
                data-testid="paywall-subscribe-button"
              >
                <CreditCard size={16} className="mr-2" />
                Voir les offres d'abonnement
              </Button>
            </div>
          )}

          {/* Grace Period Banner */}
          {gracePeriod.active && activeTab !== 'abonnement' && (
            <div className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-5 text-center" data-testid="grace-period-banner">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-1">Abonnement annule - {gracePeriod.daysLeft} jour(s) restant(s)</h3>
              <p className="text-sm text-gray-600 mb-4">
                Vos donnees seront supprimees{gracePeriod.deletionDate ? ` le ${new Date(gracePeriod.deletionDate).toLocaleDateString('fr-FR')}` : ''}.
                Reactiver votre abonnement pour conserver vos donnees.
              </p>
              <Button
                onClick={() => setActiveTab('abonnement')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-11"
                data-testid="grace-reactivate-button"
              >
                Reactiver mon abonnement
              </Button>
            </div>
          )}

          <TabsContent value="achats" className="mt-4 sm:mt-6">
            {activeTab === 'achats' && (
              <ShoppingPlannerTab
                t={t}
                recommendations={recommendedNewItemCatalog}
                suggestionGroups={SUGGESTION_DISPLAY_GROUPS}
                items={items}
                stockStatus={stockStatus}
                toBuyItems={toBuyItems}
                budgetSummary={budgetSummary}
                purchaseTransition={purchaseTransition}
                theme={currentTheme}
                activeList={shoppingPlannerActiveList}
                onActiveListChange={setShoppingPlannerActiveList}
                getSuggestionEmoji={getSuggestionEmoji}
                onPrefillSuggestion={(suggestion) => {
                  applyQuickAddSuggestion(suggestion);
                  setAddItemContext('suggestion');
                  setShowAddItem(true);
                }}
                onAddSuggestionDirect={addSuggestionDirectlyToBuyList}
                onOpenAddModal={() => {
                  resetNewItemForm();
                  setAddItemContext('manual');
                  setShowAddItem(true);
                }}
                onClearToBuyList={handleClearToBuyList}
                onQuickPurchase={handleQuickPurchase}
                onDeleteToBuyItem={(item) => deleteItem(item.item_id, item.item_nom)}
                onOpenStock={() => setActiveTab('stock')}
                onOpenBudget={() => setActiveTab('budget')}
              />
            )}
          </TabsContent>

          {/* Onglet Stock */}
          <TabsContent value="stock" className="mt-4 sm:mt-6">
            <div className="grid gap-4 sm:gap-6">
              <Card className="overflow-hidden border-0 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 shadow-xl">
                <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-end sm:justify-between">
                  <div className="max-w-2xl">
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
                      <Package className="h-4 w-4" />
                      {t('stock.title')}
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('stock.title')}</h2>
                    <p className="mt-2 text-sm leading-6 text-gray-700 sm:text-base">
                      {t('shoppingPlanner.stockDescription')}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-red-100 text-red-800">{stockSections.watch.length} A surveiller</Badge>
                    <Badge className="bg-amber-100 text-amber-800">{stockSections.inUse.length} En cours</Badge>
                    <Badge className="bg-emerald-100 text-emerald-800">{stockSections.ok.length} Stock OK</Badge>
                    <Badge className="bg-slate-100 text-slate-800">{stockSections.archive.length} Archives</Badge>
                  </div>
                </CardContent>
              </Card>

              {proactiveShortageMessages.length > 0 && (
                <Card className="border-red-200 bg-red-50/80">
                  <CardContent className="space-y-2 p-4">
                    <p className="text-sm font-semibold text-red-800">Suggestions proactives</p>
                    <div className="flex flex-col gap-1 text-sm text-red-700">
                      {proactiveShortageMessages.map((message) => (
                        <p key={message}>• {message}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{t('shoppingPlanner.stockSection')}</h2>
                <Button
                  onClick={() => {
                    setAddItemContext('stock-manual');
                    setShowAddItem(true);
                  }}
                  className="btn-modern btn-primary text-sm"
                  data-testid="add-item-button"
                >
                  <Plus size={16} className="mr-2" />
                  {t('articles.addNew')}
                </Button>
              </div>

              <Card className="border-emerald-100 bg-white/95 shadow-sm">
                <CardContent className="space-y-3 p-4">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Filtre categorie</p>
                    <div className="flex flex-wrap gap-2">
                      {availableStockCategories.map((category) => {
                        const active = stockCategoryFilters.includes(category);
                        return (
                          <button
                            key={category}
                            type="button"
                            onClick={() => toggleFilterValue(setStockCategoryFilters, category)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${active ? 'border-emerald-500 bg-emerald-100 text-emerald-800' : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-300'}`}
                          >
                            {category}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Filtre statut</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(STOCK_STATUS_LABELS).map(([statusKey, statusLabel]) => {
                        const active = stockStatusFilters.includes(statusKey);
                        return (
                          <button
                            key={statusKey}
                            type="button"
                            onClick={() => toggleFilterValue(setStockStatusFilters, statusKey)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${active ? 'border-orange-500 bg-orange-100 text-orange-800' : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'}`}
                          >
                            {statusLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Filtre type</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(STOCK_TYPE_LABELS).map(([typeKey, typeLabel]) => {
                        const active = stockTypeFilters.includes(typeKey);
                        return (
                          <button
                            key={typeKey}
                            type="button"
                            onClick={() => toggleFilterValue(setStockTypeFilters, typeKey)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${active ? 'border-cyan-500 bg-cyan-100 text-cyan-800' : 'border-gray-200 bg-white text-gray-600 hover:border-cyan-300'}`}
                          >
                            {typeLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {(stockCategoryFilters.length > 0 || stockStatusFilters.length > 0 || stockTypeFilters.length > 0) && (
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setStockCategoryFilters([]);
                          setStockStatusFilters([]);
                          setStockTypeFilters([]);
                        }}
                        className="rounded-full"
                      >
                        Reinitialiser les filtres
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <div className="grid gap-4 sm:gap-5">
                <Card className="border-red-200 bg-red-50/70">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-red-900">🔴 A surveiller (priorite)</CardTitle>
                    <CardDescription className="text-red-700">Articles proches du seuil ou rupture imminente.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-3">
                    {stockSections.watch.length === 0 ? (
                      <p className="text-sm text-red-700">Aucun article critique actuellement.</p>
                    ) : stockSections.watch.map((entry) => renderSmartStockCard(entry))}
                  </CardContent>
                </Card>

                <Card className="border-amber-200 bg-amber-50/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-amber-900">🟡 En cours d utilisation</CardTitle>
                    <CardDescription className="text-amber-700">Consommables actifs et articles bientot a recontroler.</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-3">
                    {stockSections.inUse.length === 0 ? (
                      <p className="text-sm text-amber-800">Aucun article dans cette zone.</p>
                    ) : stockSections.inUse.map((entry) => renderSmartStockCard(entry))}
                  </CardContent>
                </Card>

                <Card className="border-emerald-200 bg-emerald-50/40">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <CardTitle className="text-base text-emerald-900">🟢 Stock OK</CardTitle>
                        <CardDescription className="text-emerald-700">Articles suffisants, section repliee par defaut.</CardDescription>
                      </div>
                      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setShowStockOkSection((previous) => !previous)}>
                        {showStockOkSection ? 'Masquer' : `Afficher (${stockSections.ok.length})`}
                      </Button>
                    </div>
                  </CardHeader>
                  {showStockOkSection && (
                    <CardContent className="grid gap-3">
                      {stockSections.ok.length === 0 ? (
                        <p className="text-sm text-emerald-800">Aucun article en stock confortable pour les filtres actifs.</p>
                      ) : stockSections.ok.map((entry) => renderSmartStockCard(entry))}
                    </CardContent>
                  )}
                </Card>

                <Card className="border-slate-200 bg-slate-50/70">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <CardTitle className="text-base text-slate-900">⚪ Espace Archive</CardTitle>
                        <CardDescription className="text-slate-600">One-shot ou anciens articles, caches par defaut.</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={archiveAllStockItems}>
                          Archiver tout
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setShowArchiveSection((previous) => !previous)}>
                          {showArchiveSection ? 'Masquer' : `Afficher (${stockSections.archive.length})`}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {showArchiveSection && (
                    <CardContent className="grid gap-3">
                      {stockSections.archive.length === 0 ? (
                        <p className="text-sm text-slate-700">Aucun article archive avec les filtres actifs.</p>
                      ) : stockSections.archive.map((entry) => renderSmartStockCard(entry))}
                    </CardContent>
                  )}
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="budget" className="mt-4 sm:mt-6">
            <BudgetDashboard />
          </TabsContent>
          
          {/* Onglet Articles */}
          <TabsContent value="articles" className="mt-4 sm:mt-6">
            <div className="grid gap-4 sm:gap-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{t('articles.title')}</h2>
                <Button
                  onClick={() => {
                    setAddItemContext('manual');
                    setShowAddItem(true);
                  }}
                  className="btn-modern btn-primary text-sm"
                  data-testid="add-item-button"
                >
                  <Plus size={16} className="mr-2" />
                  {t('articles.addNew')}
                </Button>
              </div>
              
              <div className="grid gap-3 sm:gap-4">
                {[...items].reverse().map((item) => {
                  const stockItem = stockStatus.find(s => s.item_id === item.id);
                  const currentStock = stockItem?.stock_actuel || item.stock || 0;
                  const isEditing = editingQuantityId === item.id;
                  const isOwned = item.user_id === user?.id;
                  
                  return (
                    <Card 
                      key={item.id} 
                      className="card card-interactive hover:border-emerald-300" 
                      data-testid={`item-${item.id}`}
                    >
                      <CardContent className="p-3 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                            <div className="p-2 bg-cyan-100 rounded-lg shrink-0">
                              <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{item.nom}</h3>
                              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs sm:text-sm text-gray-600">
                                <span>{t('articles.category')}: {item.categorie}</span>
                                <span>{t('articles.unit')}: {item.unite}</span>
                                {item.perissable && <Badge variant="secondary" className="text-xs">{t('articles.perishable')}</Badge>}
                                {stockItem?.prix_reel != null && (
                                  <span className="text-emerald-700 font-medium" data-testid={`item-price-${item.id}`}>
                                    {stockItem.prix_reel.toFixed(2)} €
                                  </span>
                                )}
                                {stockItem?.magasin_achat && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full" data-testid={`item-store-${item.id}`}>
                                    {stockItem.magasin_achat}
                                  </span>
                                )}
                              </div>
                              
                              {/* Affichage/Édition de la quantité */}
                              <div className="flex items-center flex-wrap gap-2 mt-2">
                                {!isEditing ? (
                                  <>
                                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                                      {t('stock.stockLabel')}: <span className="text-base sm:text-lg text-emerald-600 font-bold">{currentStock} {item.unite}</span>
                                    </span>
                                    {isOwned ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingQuantityId(item.id);
                                          setTempQuantity(currentStock.toString());
                                        }}
                                        className="h-7 px-2 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
                                        title={t('articles.edit')}
                                      >
                                        <Edit3 size={12} className="mr-1" /> {t('articles.edit')}
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-gray-500">Seulement modifiable par le proprietaire</span>
                                    )}
                                  </>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <Input
                                      type="number"
                                      value={tempQuantity}
                                      onChange={(e) => setTempQuantity(e.target.value)}
                                      className="w-24 h-8 text-sm"
                                      autoFocus
                                      onKeyDown={async (e) => {
                                        if (e.key === 'Enter') {
                                          const newQty = parseFloat(tempQuantity);
                                          if (newQty >= 0) {
                                            try {
                                              await axios.put(`/items/${item.id}`, { stock: newQty });
                                              toast.success('Quantité mise à jour');
                                              loadData();
                                              setEditingQuantityId(null);
                                            } catch (error) {
                                              toast.error('Erreur lors de la mise à jour');
                                            }
                                          }
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingQuantityId(null);
                                        }
                                      }}
                                    />
                                    <span className="text-sm text-gray-600">{item.unite}</span>
                                    <Button
                                      size="sm"
                                      onClick={async () => {
                                        const newQty = parseFloat(tempQuantity);
                                        if (newQty >= 0) {
                                          try {
                                            await axios.put(`/items/${item.id}`, { stock: newQty });
                                            toast.success('✅ Quantité mise à jour');
                                            loadData();
                                            setEditingQuantityId(null);
                                          } catch (error) {
                                            toast.error('❌ Erreur lors de la mise à jour');
                                          }
                                        }
                                      }}
                                      className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white text-xs"
                                    >
                                      ✓
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditingQuantityId(null)}
                                      className="h-7 px-2 text-xs"
                                    >
                                      ✕
                                    </Button>
                                  </div>
                                )}
                              </div>
                              
                              <p className="text-xs text-emerald-500 mt-1">{t('stock.threshold')}: {item.seuil_alerte} {item.unite} | {item.consommation_jour || 0} {item.unite}/{t('stock.daysShort')} ({t('stock.totalHouseholdDay')})</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {isOwned ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedArticleForConfig(item);
                                  setShowConfigModal(true);
                                }}
                                className="border-orange-600 text-orange-600 hover:bg-orange-50 h-8 w-8 p-0"
                                title={t('articles.edit')}
                              >
                                <Settings size={14} />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-500">Action disponible uniquement sur vos articles</span>
                          )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Section Suggestions */}
              <div className="mt-8 pt-6 border-t">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{t('suggestions.title')}</h3>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {suggestedItems
                    .filter(suggested => !items.some(item => item.nom.toLowerCase() === suggested.nom.toLowerCase()))
                    .map((suggested, index) => (
                      <Card key={index} className="card card-interactive" data-testid={`suggested-item-${index}`}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{suggested.nom}</h3>
                              <p className="text-sm text-gray-600">
                                {suggested.categorie} • Seuil: {suggested.seuil} {suggested.unite}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => addSuggestedItem(suggested)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`add-suggested-${index}`}
                            >
                              <Plus size={16} />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
                
                {suggestedItems.filter(suggested => !items.some(item => item.nom.toLowerCase() === suggested.nom.toLowerCase())).length === 0 && (
                  <Card className="card">
                    <CardContent className="p-6 text-center">
                      <p className="text-gray-600">Vous avez déjà tous les articles suggérés ! 🎉</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="historique" className="mt-6">
            <HistoriqueAchats />
          </TabsContent>
          
          
          {/* Onglet Assistant IA */}
          <TabsContent value="ia" className="mt-4 sm:mt-6">
            <div className="grid gap-4 sm:gap-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{t('ai.title')}</h2>
              </div>
              
              <div className="grid gap-4">
                <Card className="card">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <Brain className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('ai.smartTitle')}</h3>
                        <p className="text-gray-600 mb-4">
                          {t('ai.smartDesc')} {t('ai.householdOf')} {user?.nb_personnes || 1} {user?.nb_personnes > 1 ? t('nav.persons') : t('nav.person')}.
                        </p>
                        <p className="text-sm text-purple-700 bg-purple-50 p-3 rounded-lg">
                          {t('ai.smartHint')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {Object.entries(aiEstimations).length > 0 && (
                  <Card className="card">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Brain className="w-5 h-5 text-purple-600" />
                        <span>{t('ai.recentEstimations')}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {Object.entries(aiEstimations).map(([itemId, estimation]) => {
                        const item = items.find(i => i.id === itemId);
                        return (
                          <div key={itemId} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-purple-900">{item?.nom}</h4>
                              <Badge variant="secondary">{estimation.confidence != null ? (estimation.confidence * 100).toFixed(0) : '0'}% {t('ai.confidence').toLowerCase()}</Badge>
                            </div>
                            <div className="text-sm text-purple-700 space-y-1">
                              <p>🥄 {t('ai.dailyConsumption')}: {estimation.daily_consumption != null ? estimation.daily_consumption.toFixed(2) : '0'} {item?.unite}/jour</p>
                              <p>🏠 {t('ai.householdOf')} {estimation.nb_personnes || 1} {estimation.nb_personnes > 1 ? t('nav.persons') : t('nav.person')}</p>
                              <p className="text-xs italic mt-2">{estimation.explanation || t('ai.referenceFallback')}</p>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Onglet Analytics */}
          <TabsContent value="analytics" className="mt-4 sm:mt-6">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="support" className="mt-4 sm:mt-6">
            {activeTab === 'support' && <FeedbackSupportTab t={t} user={user} />}
          </TabsContent>

          <TabsContent value="about" className="mt-4 sm:mt-6">
            {activeTab === 'about' && <AboutSmartInventoryTab t={t} onOpenOnboarding={() => setShowOnboarding(true)} />}
          </TabsContent>

          {/* Onglet Apprentissage */}
          <TabsContent value="apprentissage" className="mt-4 sm:mt-6">
            <div className="grid gap-4 sm:gap-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{t('learning.title')}</h2>
              </div>
              
              <div className="grid gap-4">
                <Card className="card">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-emerald-100 rounded-lg">
                        <Brain className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('learning.smartTitle')}</h3>
                        <p className="text-gray-600 mb-4">
                          {t('learning.smartDesc')} {t('ai.householdOf')} {user?.nb_personnes || 1} {user?.nb_personnes > 1 ? t('nav.persons') : t('nav.person')}.
                        </p>
                        <div className="bg-emerald-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-emerald-900 mb-2">
                            {t('learning.referencesTitle')}
                          </h4>
                          <div className="text-sm text-emerald-700 grid grid-cols-2 gap-2">
                            <div>Oeufs: {8 * (user?.nb_personnes || 1)} par semaine</div>
                            <div>Fruits: {1.5 * (user?.nb_personnes || 1)} kg par semaine</div>
                            <div>Huile: {0.33 * (user?.nb_personnes || 1)} L par semaine</div>
                            <div>Legumes: {2 * (user?.nb_personnes || 1)} kg par semaine</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {learningPatterns.length > 0 ? (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900">{t('learning.learnedPatterns')} ({learningPatterns.length} {t('articles.title').toLowerCase()})</h3>
                    <div className="grid gap-4">
                      {learningPatterns.map((pattern, index) => (
                        <Card key={index} className="card">
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <Coffee className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900">{pattern.item_name}</h4>
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <p>{t('learning.learnedConsumption')}: <span className="font-medium text-blue-600">{pattern.learned_daily_consumption.toFixed(2)} {pattern.item_unit}/jour</span></p>
                                    <p>{t('learning.baseConsumption')}: <span className="font-medium text-gray-500">{pattern.base_daily_consumption.toFixed(2)} {pattern.item_unit}/jour</span></p>
                                    <p>{t('learning.basedOn')} <span className="font-medium">{pattern.total_records}</span> {pattern.total_records > 1 ? t('learning.recordsPlural') : t('learning.record')}</p>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="text-center">
                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                  pattern.confidence_level > 0.7 ? 'bg-green-100 text-green-800' :
                                  pattern.confidence_level > 0.4 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {(pattern.confidence_level * 100).toFixed(0)}% {t('ai.confidence').toLowerCase()}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{pattern.learning_efficiency}</p>
                              </div>
                            </div>
                            
                            {/* Barre de progression visuelle */}
                            <div className="mt-4">
                              <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>{t('learning.learningLabel')}</span>
                                <span>{(pattern.confidence_level * 100).toFixed(0)}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full ${
                                    pattern.confidence_level > 0.7 ? 'bg-green-500' :
                                    pattern.confidence_level > 0.4 ? 'bg-yellow-500' :
                                    'bg-blue-500'
                                  }`}
                                  style={{ width: `${pattern.confidence_level * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <Card className="card">
                    <CardContent className="p-6 text-center">
                      <div className="p-4 bg-gray-100 rounded-lg inline-block mb-4">
                        <Brain className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('learning.noLearningYet')}</h3>
                      <p className="text-gray-600 mb-4">
                        {t('learning.noLearningStart')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t('learning.noLearningHint')}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Onglet Foyer */}
          <TabsContent value="foyer" className="mt-4 sm:mt-6">
            <div className="grid gap-4 sm:gap-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{t('household.title')}</h2>
              </div>
              <HouseholdPage />
            </div>
          </TabsContent>

          {/* Onglet Abonnement */}
          <TabsContent value="abonnement" className="mt-4 sm:mt-6">
            <div className="grid gap-4 sm:gap-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{t('subscription.title')}</h2>
              </div>
              <SubscriptionPage />
            </div>
          </TabsContent>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Tabs>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] sm:hidden">
        <div
          className="mx-auto grid w-full max-w-md grid-cols-3 items-stretch gap-2 rounded-2xl border p-2 backdrop-blur-lg"
          style={{
            backgroundColor: currentTheme.bottomBarBackground,
            borderColor: currentTheme.bottomBarBorder,
            boxShadow: '0 18px 35px -20px rgba(2, 6, 23, 0.9)',
          }}
          data-testid="mobile-bottom-actions"
        >
          <Button
            type="button"
            variant="ghost"
            className="h-12 min-w-0 flex-col gap-1 rounded-xl px-1 text-[11px] leading-4 text-white hover:bg-white/10"
            onClick={() => openBarcodeFlow()}
            data-testid="mobile-action-barcode"
          >
            <Package className="h-4 w-4" />
            Scan code-barres
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-12 min-w-0 flex-col gap-1 rounded-xl px-1 text-[11px] leading-4 text-white hover:bg-white/10"
            onClick={() => setShowReceiptIntroPopup(true)}
            data-testid="mobile-action-receipt"
          >
            <Mail className="h-4 w-4" />
            Scan ticket
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-12 min-w-0 flex-col gap-1 rounded-xl px-1 text-[11px] leading-4 text-white hover:bg-white/10"
            onClick={() => setShowChangePassword(true)}
            data-testid="mobile-action-settings"
            aria-label="Settings"
            title="Settings"
          >
            <Settings className="h-4 w-4" />
            Reglages
          </Button>
        </div>
      </div>

      <SmartOnboardingModal open={showOnboarding} onClose={closeOnboarding} t={t} />

      {/* Modal Ajout Article */}
      {showAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-3 sm:p-4" onClick={() => setShowAddItem(false)}>
          <Card className="w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden" data-testid="add-item-modal" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{t('newItem.modalTitle')}</CardTitle>
              <CardDescription>{t('newItem.modalDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto pb-4">
              <form onSubmit={handleAddItem} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nom">{t('newItem.nameLabel')}</Label>
                  <Input
                    id="nom"
                    placeholder={t('newItem.namePlaceholder')}
                    value={newItem.nom}
                    onChange={(e) => {
                      const newNom = e.target.value;
                      setNewItem({...newItem, nom: newNom});
                      
                      // Vérifier si l'article est connu après un délai
                      if (newNom.length >= 3) {
                        setTimeout(() => checkArticleKnown(newNom, newItem.unite, user?.nb_personnes), 500);
                      } else {
                        setArticleKnown(null);
                        setShowConsumptionInput(false);
                      }
                    }}
                    className="text-base"
                    required
                  />
                  <p className="text-xs text-gray-500">
                    {t('newItem.inputHelp')}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('form.category')}</Label>
                  <Select
                    value={
                      PREDEFINED_ITEM_CATEGORIES.some(c => c.value === newItem.categorie) || customCategories.includes(newItem.categorie)
                        ? newItem.categorie
                        : 'alimentation'
                    }
                    onValueChange={(value) => {
                      if (value === '_add_new') {
                        setCatInputFor('new');
                        setCatInputValue('');
                      } else {
                        setNewItem({ ...newItem, categorie: value });
                        setCatInputFor(null);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="item-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREDEFINED_ITEM_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                      {customCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </SelectItem>
                      ))}
                      <SelectItem value="_add_new" className="text-emerald-700 font-medium border-t mt-1 pt-1">
                        ➕ Ajouter une catégorie
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {catInputFor === 'new' && (
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        autoFocus
                        type="text"
                        placeholder="Ex: sport, pharmacie, bébé…"
                        value={catInputValue}
                        onChange={(e) => setCatInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && confirmCustomCategory('new')}
                        maxLength={40}
                        className="text-sm"
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        onClick={() => confirmCustomCategory('new')}
                        disabled={!catInputValue.trim()}
                      >✓</Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => { setCatInputFor(null); setCatInputValue(''); }}
                      >✕</Button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="initial_quantite">{t('newItem.initialQuantity')}</Label>
                  <Input
                    id="initial_quantite"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="Ex: 2.5"
                    value={newItem.initial_quantite}
                    onChange={(e) => setNewItem({...newItem, initial_quantite: e.target.value})}
                    data-testid="new-item-quantity-input"
                  />
                  <p className="text-xs text-gray-500">Optionnel: laissez vide pour ajouter uniquement a la liste a acheter.</p>
                </div>

                {addItemContext === 'suggestion' && (
                  <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
                    Ajout depuis suggestion: on demande uniquement la quantite ici. Unite et seuil d alerte peuvent etre ajustes ensuite.
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {addItemContext !== 'suggestion' && (
                    <div className="space-y-2">
                      <Label>{t('form.unit')}</Label>
                      <Select 
                        value={newItem.unite} 
                        onValueChange={(value) => setNewItem({...newItem, unite: value})}
                      >
                        <SelectTrigger data-testid="item-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="g">g</SelectItem>
                          <SelectItem value="L">L</SelectItem>
                          <SelectItem value="ml">ml</SelectItem>
                          <SelectItem value="pièces">pièces</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Affichage si article reconnu */}
                  {articleKnown && articleKnown.known && (
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 font-medium">
                        {t('newItem.knownTitle')}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        {articleKnown.suggestion_text}
                      </p>
                    </div>
                  )}

                  {/* Champ d'estimation manuelle si article inconnu */}
                  {showConsumptionInput && (
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <div className="mb-3">
                        <p className="text-sm text-yellow-800 font-medium">
                          {t('newItem.unknownTitle')}
                        </p>
                        <p className="text-xs text-yellow-700 mt-1">
                          {t('newItem.unknownDescription')}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('newItem.dailyQuantity')}</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            placeholder="Ex: 2.5"
                            value={newItem.consommation_estimee}
                            onChange={(e) => setNewItem({...newItem, consommation_estimee: e.target.value})}
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">{t('form.unit')}</Label>
                          <Select 
                            value={newItem.consommation_unite} 
                            onValueChange={(value) => setNewItem({...newItem, consommation_unite: value})}
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder={t('form.unit')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="g">g (grammes)</SelectItem>
                              <SelectItem value="kg">kg (kilogrammes)</SelectItem>
                              <SelectItem value="mL">mL (millilitres)</SelectItem>
                              <SelectItem value="L">L (litres)</SelectItem>
                              <SelectItem value="pièces">pièces</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  {addItemContext !== 'suggestion' && (
                    <div className="space-y-2">
                      <Label htmlFor="seuil_alerte">{t('form.alertThreshold')}</Label>
                      <Input
                        id="seuil_alerte"
                        type="number"
                        step="0.1"
                        value={newItem.seuil_alerte}
                        onChange={(e) => setNewItem({...newItem, seuil_alerte: e.target.value})}
                        data-testid="item-threshold-input"
                      />
                      <p className="text-xs text-gray-500">Optionnel: valeur par defaut automatique = 1.</p>
                    </div>
                  )}
                </div>
                
                <div className="sticky bottom-0 flex space-x-2 justify-end bg-white pt-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      resetNewItemForm();
                      setShowAddItem(false);
                    }}
                    data-testid="cancel-add-item"
                  >
                    {t('form.cancel')}
                  </Button>
                  <Button type="submit" data-testid="submit-add-item">
                    {t('form.add')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Configuration Articles en Batch */}
      {showBatchArticlesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle>Configurer les articles sélectionnés</CardTitle>
              <CardDescription>Modifiez les paramètres avant de valider l'ajout</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto pb-4 space-y-4">
              {batchArticlesConfig.map((config, idx) => (
                <div key={idx} className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-lg text-gray-900">{config.nom}</h4>
                    <span className="text-sm text-gray-500">{config.categorie}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Quantité</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={config.initial_quantite}
                        onChange={(e) => {
                          const newConfig = [...batchArticlesConfig];
                          newConfig[idx].initial_quantite = e.target.value;
                          setBatchArticlesConfig(newConfig);
                        }}
                        placeholder="Ex: 2.5"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Unité</Label>
                      <div className="flex items-center justify-center h-9 px-3 border rounded-md bg-white text-sm text-gray-600">
                        {config.unite}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Seuil d'alerte</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={config.seuil_alerte}
                        onChange={(e) => {
                          const newConfig = [...batchArticlesConfig];
                          newConfig[idx].seuil_alerte = e.target.value;
                          setBatchArticlesConfig(newConfig);
                        }}
                      />
                    </div>
                  </div>

                </div>
              ))}
            </CardContent>
            <div className="border-t p-4 flex gap-2 justify-end bg-white">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBatchArticlesModal(false);
                  setBatchArticlesConfig([]);
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleBatchAddArticles}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                ✅ Valider et ajouter {batchArticlesConfig.length} article{batchArticlesConfig.length > 1 ? 's' : ''}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Mouvement */}
      {showMovement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="add-movement-modal">
            <CardHeader>
              <CardTitle>Nouveau mouvement</CardTitle>
              <CardDescription>Enregistrer un achat ou une consommation</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddMovement} className="space-y-4">
                <div className="space-y-2">
                  <Label>Article</Label>
                  <Select 
                    value={newMovement.item_id} 
                    onValueChange={(value) => setNewMovement({...newMovement, item_id: value})}
                  >
                    <SelectTrigger data-testid="movement-item-select">
                      <SelectValue placeholder="Sélectionner un article" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.nom} ({item.unite})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select 
                      value={newMovement.type} 
                      onValueChange={(value) => setNewMovement({...newMovement, type: value})}
                    >
                      <SelectTrigger data-testid="movement-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACHAT">Achat</SelectItem>
                        <SelectItem value="CONSOMMATION">Consommation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="quantite">Quantité</Label>
                    <Input
                      id="quantite"
                      type="number"
                      step="0.1"
                      value={newMovement.quantite}
                      onChange={(e) => setNewMovement({...newMovement, quantite: e.target.value})}
                      required
                      data-testid="movement-quantity-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="note">Note (optionnel)</Label>
                  <Input
                    id="note"
                    value={newMovement.note}
                    onChange={(e) => setNewMovement({...newMovement, note: e.target.value})}
                    placeholder="Ex: Courses du samedi"
                    data-testid="movement-note-input"
                  />
                </div>
                
                <div className="flex space-x-2 justify-end">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowMovement(false)}
                    data-testid="cancel-add-movement"
                  >
                    Annuler
                  </Button>
                  <Button type="submit" data-testid="submit-add-movement">
                    Enregistrer
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Saisie Quantité Achat */}
      {showQuantityPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowQuantityPopup(false); setSelectedItemForPurchase(null); }}>
          <Card className="w-full max-w-sm" data-testid="quantity-popup-modal" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <ShoppingCart className="w-5 h-5 text-green-600" />
                    <span>Quantité d'achat</span>
                  </CardTitle>
                  <CardDescription>
                    {selectedPurchaseDisplayName}
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowQuantityPopup(false);
                    setSelectedItemForPurchase(null);
                    setPurchaseQuantity('');
                  }}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                  title="Fermer"
                >
                  <X size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="purchase_quantity">Quantité à ajouter</Label>
                  <Input
                    id="purchase_quantity"
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={purchaseQuantity}
                    onChange={(e) => setPurchaseQuantity(e.target.value)}
                    placeholder="Ex: 2.5"
                    className="text-center text-lg"
                    data-testid="purchase-quantity-input"
                    autoFocus
                  />
                  {selectedItemForPurchase && (
                    <p className="text-sm text-gray-600 text-center">
                      Unité: {selectedPurchaseDisplayUnit}
                    </p>
                  )}
                </div>
                
                <div className="flex space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowQuantityPopup(false);
                      setSelectedItemForPurchase(null);
                      setPurchaseQuantity('');
                    }}
                    className="flex-1"
                    data-testid="cancel-purchase"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={confirmPurchase}
                    disabled={isProcessing}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                    data-testid="confirm-purchase"
                  >
                    <ShoppingCart size={16} className="mr-2" />
                    {isProcessing ? 'En cours...' : 'Ajouter'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Édition Article */}
      {showEditItem && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowEditItem(false); setEditingItem(null); }}>
          <Card className="w-full max-w-md" data-testid="edit-item-modal" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Utensils className="w-5 h-5 text-emerald-600" />
                <span>Modifier l'article</span>
              </CardTitle>
              <CardDescription>
                Ajustez le seuil d'alarme et autres paramètres
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateItem} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_nom">Nom de l'article</Label>
                  <Input
                    id="edit_nom"
                    value={editingItem.nom}
                    onChange={(e) => setEditingItem({...editingItem, nom: e.target.value})}
                    required
                    data-testid="edit-item-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_categorie">Catégorie</Label>
                  <Select
                    value={
                      PREDEFINED_ITEM_CATEGORIES.some(c => c.value === editingItem.categorie) || customCategories.includes(editingItem.categorie)
                        ? editingItem.categorie
                        : 'alimentation'
                    }
                    onValueChange={(value) => {
                      if (value === '_add_new') {
                        setCatInputFor('edit');
                        setCatInputValue('');
                      } else {
                        setEditingItem({ ...editingItem, categorie: value });
                        setCatInputFor(null);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="edit-item-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREDEFINED_ITEM_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                      {customCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </SelectItem>
                      ))}
                      <SelectItem value="_add_new" className="text-emerald-700 font-medium border-t mt-1 pt-1">
                        ➕ Ajouter une catégorie
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {catInputFor === 'edit' && (
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        autoFocus
                        type="text"
                        placeholder="Ex: sport, pharmacie, bébé…"
                        value={catInputValue}
                        onChange={(e) => setCatInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && confirmCustomCategory('edit')}
                        maxLength={40}
                        className="text-sm"
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        onClick={() => confirmCustomCategory('edit')}
                        disabled={!catInputValue.trim()}
                      >✓</Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => { setCatInputFor(null); setCatInputValue(''); }}
                      >✕</Button>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unité</Label>
                    <Select 
                      value={editingItem.unite} 
                      onValueChange={(value) => setEditingItem({...editingItem, unite: value})}
                    >
                      <SelectTrigger data-testid="edit-item-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="L">L</SelectItem>
                        <SelectItem value="ml">ml</SelectItem>
                        <SelectItem value="pièces">pièces</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit_seuil_alerte" className="text-emerald-700 font-semibold">
                      ⚠️ Seuil d'alerte
                    </Label>
                    <Input
                      id="edit_seuil_alerte"
                      type="number"
                      step="0.1"
                      value={editingItem.seuil_alerte}
                      onChange={(e) => setEditingItem({...editingItem, seuil_alerte: e.target.value})}
                      required
                      className="border-emerald-300 focus:border-emerald-500"
                      data-testid="edit-item-threshold-input"
                    />
                  </div>
                </div>
                
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-sm text-emerald-700">
                    💡 <strong>Seuil intelligent :</strong> L'IA ajustera ce seuil selon vos habitudes de consommation pour optimiser les alertes.
                  </p>
                </div>
                
                <div className="flex space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowEditItem(false);
                      setEditingItem(null);
                    }}
                    className="flex-1"
                    data-testid="cancel-edit-item"
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="submit-edit-item"
                  >
                    Sauvegarder
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Édition Profil */}
      {showEditProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditProfile(false)}>
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-profile-modal" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span>Modifier le foyer</span>
              </CardTitle>
              <CardDescription>
                Ajustez la taille de votre foyer pour des estimations précises
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_nb_personnes" className="text-blue-700 font-semibold">
                    👥 Nombre de personnes dans le foyer
                  </Label>
                  <Input
                    id="edit_nb_personnes"
                    type="number"
                    min="1"
                    max="20"
                    value={editProfileData.nb_personnes}
                    onChange={(e) => setEditProfileData({...editProfileData, nb_personnes: e.target.value})}
                    required
                    className="text-center text-lg border-blue-300 focus:border-blue-500"
                    data-testid="edit-nb-personnes-input"
                  />
                </div>
                
                <div className="space-y-3">
                  <Label className="text-blue-700 font-semibold">
                    📅 Jours de réception des rapports
                  </Label>
                  
                  <div className="grid grid-cols-4 gap-2">
                    {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map((day) => (
                      <Button
                        key={day}
                        type="button"
                        variant={editProfileData.report_days?.includes(day) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleDayToggle(day)}
                        className={`text-xs ${editProfileData.report_days?.includes(day) ? 'bg-blue-600 text-white' : ''}`}
                      >
                        {day.substring(0, 3)}
                      </Button>
                    ))}
                    <Button
                      type="button"
                      variant={editProfileData.report_days?.length === 7 ? 'default' : 'outline'}
                      size="sm"
                      onClick={handleAllDaysToggle}
                      className={`text-xs ${editProfileData.report_days?.length === 7 ? 'bg-green-600 text-white' : ''}`}
                    >
                      Tous
                    </Button>
                  </div>

                  <div className="space-y-2 mt-3">
                    <Label htmlFor="edit_report_time" className="text-blue-700 font-semibold">
                      ⏰ Heure d'envoi
                    </Label>
                    <Input
                      id="edit_report_time"
                      type="time"
                      value={editProfileData.report_time}
                      onChange={(e) => setEditProfileData({...editProfileData, report_time: e.target.value})}
                      required
                      className="text-center text-lg border-blue-300 focus:border-blue-500"
                    />
                  </div>

                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-700">
                      📧 Vous recevrez un rapport par email les <strong>{editProfileData.report_days?.length > 0 ? editProfileData.report_days.join(', ') : 'jours sélectionnés'}</strong> à <strong>{editProfileData.report_time}</strong>
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      💡 Par défaut : Mercredi et Vendredi à 12h
                    </p>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-700 text-center">
                    🧠 <strong>Impact intelligent :</strong> Ces changements personnalisent votre expérience et optimisent les estimations de consommation.
                  </p>
                </div>
                
                <div className="flex space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowEditProfile(false)}
                    className="flex-1"
                    data-testid="cancel-edit-profile"
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="submit-edit-profile"
                  >
                    Sauvegarder
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scanner de code-barres */}
      {showBarcodePermissionPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowBarcodePermissionPopup(false)}>
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-emerald-600" />
                Autorisation caméra requise
              </CardTitle>
              <CardDescription>
                La caméra est nécessaire uniquement pour scanner les codes-barres de vos articles
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3 text-sm text-cyan-800">
                ℹ️ Cette permission est demandée au moment du scan et n'est pas utilisée pour d'autres finalités.
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Que souhaitez-vous faire ?</p>
                
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-3 px-4 hover:bg-blue-50 border-blue-200"
                    onClick={() => requestBarcodeCameraPermission('once')}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-medium text-blue-700">Autoriser juste pour cette fois</span>
                      <span className="text-xs text-gray-600">Sera redemandé au prochain scan</span>
                    </div>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-3 px-4 hover:bg-emerald-50 border-emerald-200"
                    onClick={() => requestBarcodeCameraPermission('always')}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-medium text-emerald-700">Autoriser toujours</span>
                      <span className="text-xs text-gray-600">Pas besoin de redemander</span>
                    </div>
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowBarcodePermissionPopup(false);
                    setCameraPermissionChoice(null);
                  }}
                >
                  Refuser
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => openBarcodeFlow({ manualOnly: true })}
                >
                  Saisie manuelle
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showBarcodeScanner && (
        <BarcodeScanner
          startInManualMode={barcodeStartManualMode}
          onItemAdded={() => {
            loadData(); // Recharger les données après ajout
            setBarcodeStartManualMode(false);
            setShowBarcodeScanner(false);
          }}
          onClose={() => {
            setBarcodeStartManualMode(false);
            setShowBarcodeScanner(false);
          }}
        />
      )}

      {showReceiptIntroPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowReceiptIntroPopup(false)}>
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>Scanner votre ticket 🧾</CardTitle>
              <CardDescription>
                Prenez en photo votre ticket de caisse. L'application detectera automatiquement les articles et les ajoutera a votre stock.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3 text-xs text-cyan-800">
                Permission requise: camera/photos uniquement pour scanner vos tickets. Cette permission n'est jamais utilisee hors de cette fonctionnalite.
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowReceiptIntroPopup(false)}>
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                  onClick={() => {
                    setShowReceiptIntroPopup(false);
                    setShowReceiptScanner(true);
                  }}
                >
                  Continuer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scanner de ticket de caisse */}
      {showReceiptScanner && (
        <ReceiptScanner
          onItemsAdded={() => {
            loadData(); // Recharger les données après ajout
            setShowReceiptScanner(false);
          }}
          onClose={() => setShowReceiptScanner(false)}
        />
      )}

      <FoodEducationSheetModal open={Boolean(educationSheet)} sheet={educationSheet} onClose={() => setEducationSheet(null)} />

      {/* Email configuration modal removed - now handled via environment variables */}

      {/* Modal Changement de mot de passe */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowChangePassword(false)}>
          <Card className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto" data-testid="change-password-modal" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <span>Paramètres du compte</span>
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowChangePassword(false);
                    setChangePasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                  }}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                  data-testid="close-account-modal"
                  title="Fermer"
                >
                  <X size={16} />
                </Button>
              </div>
              <CardDescription>
                Changement de mot de passe et paramètres
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <form onSubmit={handleChangePassword} className="space-y-3">
                <div className="rounded-2xl border p-3" style={{ borderColor: currentTheme.surfaceBorder, backgroundColor: currentTheme.panelBackground }}>
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold" style={{ color: currentTheme.surfaceText }}>Theme du dashboard</h4>
                    <p className="mt-1 text-xs" style={{ color: currentTheme.mutedText }}>Choisissez l ambiance de la navigation verticale, de la page et de la barre d actions.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {Object.entries(DASHBOARD_THEMES).map(([themeKey, theme]) => (
                      <button
                        key={themeKey}
                        type="button"
                        onClick={() => setDashboardTheme(themeKey)}
                        className={`rounded-2xl border p-3 text-left transition ${dashboardTheme === themeKey ? 'shadow-lg' : 'hover:-translate-y-0.5 hover:shadow-sm'}`}
                        style={{
                          borderColor: dashboardTheme === themeKey ? theme.accent : currentTheme.surfaceBorder,
                          background: theme.heroBackground,
                          color: theme.surfaceText,
                        }}
                      >
                        <div className="flex gap-1.5">
                          {theme.preview.map((color) => (
                            <span key={color} className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: color }} />
                          ))}
                        </div>
                        <p className="mt-3 text-sm font-semibold">{theme.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border p-3" style={{ borderColor: currentTheme.surfaceBorder, backgroundColor: currentTheme.panelBackground }}>
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold" style={{ color: currentTheme.surfaceText }}>Langue de l application</h4>
                    <p className="mt-1 text-xs" style={{ color: currentTheme.mutedText }}>Changez rapidement la langue sans quitter les reglages.</p>
                  </div>
                  <LanguageSwitcher />
                </div>

                <div className="bg-emerald-50 p-2 rounded-lg">
                  <h4 className="text-sm font-medium text-emerald-900 mb-1">👤 Informations du compte</h4>
                  <div className="text-xs text-emerald-700 space-y-0.5">
                    <p>Utilisateur: <span className="font-medium">{user?.username}</span></p>
                    <p>Email: <span className="font-medium">{user?.email}</span></p>
                    <p>Type: <span className="font-medium">{user?.user_type}</span></p>
                  </div>
                </div>

                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">🔐 Confidentialite et droits RGPD</h4>
                  <p className="text-xs text-blue-700 mb-3">
                    Consultez la politique de confidentialite et supprimez votre compte a tout moment.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
                      data-testid="open-privacy-policy-settings"
                    >
                      <ShieldCheck size={14} className="mr-2" />
                      Politique de confidentialite
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      className="flex-1"
                      onClick={() => { window.location.href = '/delete-account'; }}
                      data-testid="open-delete-account"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Supprimer mon compte
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">🔒 Changer le mot de passe</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Mot de passe actuel</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={changePasswordData.current_password}
                      onChange={(e) => setChangePasswordData({...changePasswordData, current_password: e.target.value})}
                      required
                      data-testid="current-password-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new_password">Nouveau mot de passe</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={changePasswordData.new_password}
                      onChange={(e) => setChangePasswordData({...changePasswordData, new_password: e.target.value})}
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={changePasswordData.confirm_password}
                      onChange={(e) => setChangePasswordData({...changePasswordData, confirm_password: e.target.value})}
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">
                      💡 Le mot de passe doit contenir au moins 6 caractères
                    </p>
                  </div>

                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    🔒 Changer le mot de passe
                  </Button>
                </div>
              </form>
              
              {/* Section Danger Zone */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4">
                  <h4 className="text-sm font-semibold text-orange-800 mb-2">🗂️ Supprimer certaines donnees (sans supprimer le compte)</h4>
                  <p className="text-xs text-orange-700 mb-3">
                    Selectionnez les categories de donnees a supprimer definitivement. Votre compte et vos articles seront conserves.
                  </p>
                  <PartialDataDelete />
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="text-sm font-semibold text-red-800 mb-2">⚠️ Zone Danger</h4>
                  <p className="text-xs text-red-700 mb-3">
                    Cette action supprimera TOUTES vos données (articles, mouvements, achats). Cette action est irréversible!
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={async () => {
                      if (window.confirm(t('purge.confirmMessage'))) {
                        try {
                          // Fermer le modal AVANT de lancer la purge
                          setShowChangePassword(false);
                          setShowEditProfile(false);
                          
                          const response = await axios.delete('/items/purge-all-data');
                          if (response.data.success) {
                            toast.success(response.data.message || t('purge.success'));
                            setItems([]);
                            setMovements([]);
                            setStockStatus([]);
                            setLearningPatterns([]);
                            setAiEstimations({});
                            resetNewItemForm();
                            setNewMovement({ item_id: '', type: 'ACHAT', quantite: '', source: '', note: '' });
                            setEditingQuantityId(null);
                            setTempQuantity('');
                            setSelectedItemForPurchase(null);
                            setPurchaseQuantity('');
                            setEditingItem(null);
                            setShowEditItem(false);
                            setShowMovement(false);
                            setShowQuantityPopup(false);
                            setActiveTab('stock');
                            try { await loadData(); } catch(e) { /* paywall ok */ }
                          }
                        } catch (error) {
                          console.error('Erreur purge:', error);
                          if (error.response?.status === 403) {
                            toast.error('Abonnement requis pour cette action');
                          } else {
                            toast.error(t('purge.error'));
                          }
                        }
                      }
                    }}
                    className="w-full bg-red-600 hover:bg-red-700"
                    data-testid="purge-button"
                  >
                    <Trash2 size={16} className="mr-2" />
                    {t('purge.button')}
                  </Button>
                </div>
              </div>
              
              <div className="flex space-x-2 mt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowChangePassword(false);
                    setChangePasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                  }}
                  className="flex-1"
                  data-testid="cancel-change-password"
                >
                  Fermer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteConfirmation({show: false, itemId: null, itemName: ''})}>
          <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2 text-red-600">
                  <Trash2 className="w-5 h-5" />
                  <span>Confirmer la suppression</span>
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelDelete}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                  title="Fermer"
                >
                  <X size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-6">
                Êtes-vous sûr de vouloir supprimer <strong>"{deleteConfirmation.itemName}"</strong> ?
              </p>
              <p className="text-sm text-gray-600 mb-6">
                Cette action supprimera aussi l'historique des mouvements et ne peut pas être annulée.
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={cancelDelete}
                  variant="outline"
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal d'estimation de consommation */}
      <EstimationModal
        isOpen={showEstimationModal}
        onClose={() => setShowEstimationModal(false)}
        productInfo={estimationProductInfo}
        onValidate={handleEstimationValidate}
      />

      {/* Modal de configuration article */}
      <ConfigurationArticleModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        article={selectedArticleForConfig}
        onSuccess={() => {
          loadData();
          setShowConfigModal(false);
        }}
      />

      {/* Modal Prix et Magasin réutilisable */}
      <PrixMagasinModal
        isOpen={showPrixMagasinModal}
        onClose={() => {
          setShowPrixMagasinModal(false);
          setPrixMagasinDefaultPrice(null);
          setPendingPurchaseMovementId(null);
        }}
        productInfo={prixMagasinProductInfo}
        itemId={prixMagasinProductInfo?.item_id}
        movementId={pendingPurchaseMovementId}
        defaultPrice={prixMagasinDefaultPrice}
        onSuccess={() => {
          loadData();
          setPendingPurchaseMovementId(null);
        }}
      />

      {showDeleteAccountConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" onClick={() => !isDeletingAccount && setShowDeleteAccountConfirm(false)}>
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-red-700">Supprimer votre compte</CardTitle>
              <CardDescription>
                Etes-vous sur de vouloir supprimer votre compte ? Cette action est irreversible et supprimera toutes vos donnees.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="delete-account-password">Mot de passe de confirmation</Label>
                <Input
                  id="delete-account-password"
                  type="password"
                  value={deleteAccountPassword}
                  onChange={(e) => setDeleteAccountPassword(e.target.value)}
                  placeholder="Saisissez votre mot de passe"
                  data-testid="delete-account-password"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={isDeletingAccount}
                  onClick={() => setShowDeleteAccountConfirm(false)}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="flex-1"
                  disabled={isDeletingAccount}
                  onClick={handleDeleteAccount}
                  data-testid="confirm-delete-account"
                >
                  {isDeletingAccount ? 'Suppression...' : 'Supprimer mon compte'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ParticulierDashboard;